import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import {
    BulbOutlined,
    InfoCircleOutlined,
    ArrowDownOutlined,
    ArrowRightOutlined,
    ArrowUpOutlined,
    ReloadOutlined,
} from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { Card, Button, Empty, Spin, Modal, Tooltip, Select, DatePicker } from 'antd';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef } from 'ag-grid-community';
import dayjs, { Dayjs } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { useNavigate } from 'react-router-dom';
import ReactEcharts from 'echarts-for-react';
import * as echarts from 'echarts';
import ExcelJS from 'exceljs';
import { IAppComponentProps } from '../../../../../components';
import { getI18n } from '../../../../../utils/Utils';
import { EimExecutiveDashboardRequest } from '../../../../../__generated__/types';
import {
    QUERY_EIM_EXECUTIVE_DASHBOARD_SYSTEM_METRICS,
    QUERY_EIM_EXECUTIVE_DASHBOARD_GROUP_METRICS,
} from '../../dashboardQueries';
import {
    QUERY_EIM_EXECUTIVE_DASHBOARD_V2_COUNTRY_DETAIL,
    QUERY_EIM_EXECUTIVE_DASHBOARD_V2_COUNTRY_METRICS,
} from './eimExecutiveDashboardV2Queries';
import { EIM_THEME_STORAGE_KEY } from '../../../themePreference';
import { buildCompanyCountryLookup, getCountryFlagUrl, resolveCountryCode } from '../../eimDashboardUtils';
import CountryFlagLabel from '../../components/CountryFlagLabel';
import worldGeoJson from '../../../../../assets/maps/world.json';
import worldEditableShapeDataset from '../../docs/worldEditableShapeDataset.json';
import styles from './eimExecutiveDashboardV2.module.scss';

dayjs.extend(quarterOfYear);
import { useAppDispatch, useAppSelector } from '../../../../../main/hooks';
import {
    setCountryCode,
    setCustomPeriodRange,
    setPeriodOption,
    setTileFilters,
} from '../../eimDashboardFiltersSlice';

type ThemeMode = 'dark' | 'light';

type TrendTone = 'positive' | 'negative';

type TopMetricDefinition = {
    key: string;
    infoKey: string;
    value: () => string;
    trend: () => string;
    hint?: () => string;
    trendTone: TrendTone;
    showTrendArrow?: boolean;
    sparklineData?: number[];
    miniVisualKind?: 'ring' | 'line' | 'bars' | 'progress';
    miniVisualData?: number[];
    miniVisualValue?: number;
    onClick?: () => void;
    isClickable?: boolean;
};

type CountryMetric = {
    country?: string | null;
    companyCode?: string | null;
    intake?: {
        totalProcessed?: number | null;
        inboundCount?: number | null;
        outboundCount?: number | null;
    } | null;
    processing?: {
        stpRate?: number | null;
        onTimeRate?: number | null;
        avgTransitDays?: number | null;
        inTransitCount?: number | null;
    } | null;
    exceptions?: {
        failedCount?: number | null;
        overdueCount?: number | null;
        failureRate?: number | null;
    } | null;
    coverage?: {
        activePartners?: number | null;
        totalPartners?: number | null;
        coverageRate?: number | null;
    } | null;
};

type CountryErrorMessageRow = {
    direction?: string | null;
    message?: string | null;
    count?: number | null;
    percentage?: number | null;
};

type GroupMetric = {
    groupName?: string | null;
    country?: string | null;
    totalInvoices?: number | null;
    successfulCount?: number | null;
    inTransitCount?: number | null;
    failedCount?: number | null;
};

type SystemMetric = {
    systemId?: number | null;
    systemLabel?: string | null;
    intake?: {
        totalProcessed?: number | null;
        inboundCount?: number | null;
        outboundCount?: number | null;
    } | null;
    processing?: {
        stpRate?: number | null;
        onTimeRate?: number | null;
        avgTransitDays?: number | null;
        inTransitCount?: number | null;
    } | null;
    exceptions?: {
        failedCount?: number | null;
        overdueCount?: number | null;
        failureRate?: number | null;
    } | null;
    coverage?: {
        activePartners?: number | null;
        totalPartners?: number | null;
        coverageRate?: number | null;
    } | null;
};

type PartnerSummaryRow = {
    partnerno?: string | null;
    partnerName?: string | null;
    total?: number | null;
    company?: string | null;
    direction?: string | null;
};

type PartnerVolumeRow = {
    partnerno: string | null;
    partnerName: string | null;
    company: string | null;
    inboundVolume: number;
    outboundVolume: number;
    unknownVolume: number;
    totalVolume: number;
    sharePercent: number;
};

type HourlyTrendRow = {
    hourLabel?: string | null;
    successfulCount?: number | null;
    failedCount?: number | null;
};

type CountryDetailMetrics = {
    totalProcessed?: number | null;
    successfulCount?: number | null;
    failedCount?: number | null;
    overdueCount?: number | null;
    inTransitCount?: number | null;
    stpRate?: number | null;
    failureRate?: number | null;
    avgTransitDays?: number | null;
};

type WorldMapPoint = {
    countryCode: string;
    name: string;
    totalProcessed: number;
    stpRate: number;
    avgTransitDays: number;
    failedCount: number;
    overdueCount: number;
    inTransitCount: number;
    value: number;
};

type WorldMapSeriesPoint = WorldMapPoint & {
    healthCategory: WorldMapHealthCategory;
    failureRate: number;
    inTransitRate: number;
    itemStyle: {
        areaColor: string;
    };
};

type WorldMapHealthCategory = 'healthy' | 'warning' | 'critical' | 'noData';

type IncidentSeverity = 'critical' | 'warning';

type IncidentRow = {
    key: string;
    severity: IncidentSeverity;
    label: string;
    detail: string;
};

type TopCountryRow = WorldMapPoint & {
    rank: number;
    progress: number;
};

type VolumeLeaderboardRow = {
    rank: number;
    code: string;
    name: string;
    totalProcessed: number;
    totalErrors: number;
    errorRate: number;
    progress: number;
    countryCode?: string;
};

type UnmappedFailureRow = {
    key: string;
    countryLabel: string;
    companyCode: string;
    failures: number;
};

type CountryVolumeSortKey = 'name' | 'volume';
type CountryVolumeSortDirection = 'asc' | 'desc';

type PeriodOption = 'today' | 'currentWeek' | 'currentMonth' | 'currentQuarter' | 'currentYear' | 'custom';

const GEO_MAP_WINDOW_HOURS = 24;
const DEFAULT_PERIOD_OPTION: PeriodOption = 'today';
const { RangePicker } = DatePicker;

const WORLD_MAP_HEALTH_COLORS: Record<WorldMapHealthCategory, string> = {
    healthy: '#6ccf8d',
    warning: '#f5b436',
    critical: '#ea5b5b',
    noData: '#d8dde6',
};

const TREND_SUCCESS_COLOR = '#16a34a';
const TREND_FAILED_COLOR = '#ef4444';

const HEALTH_CATEGORY_I18N_KEY: Record<WorldMapHealthCategory, string> = {
    healthy: 'eim-world-map-health-healthy',
    warning: 'eim-world-map-health-warning',
    critical: 'eim-world-map-health-critical',
    noData: 'eim-world-map-health-no-data',
};

const SYSTEM_STATUS_I18N_KEY: Record<WorldMapHealthCategory, string> = {
    healthy: 'eim-v2d-system-status-healthy',
    warning: 'eim-v2d-system-status-warning',
    critical: 'eim-v2d-system-status-critical',
    noData: 'eim-world-map-health-no-data',
};

const getHealthDefaultLabel = (category: WorldMapHealthCategory): string => {
    if (category === 'healthy') {
        return 'Healthy';
    }
    if (category === 'warning') {
        return 'Warning';
    }
    if (category === 'critical') {
        return 'Critical';
    }
    return 'No data';
};

const getSystemStatusDefaultLabel = (category: WorldMapHealthCategory): string => {
    if (category === 'healthy') {
        return 'All systems operational';
    }
    if (category === 'warning') {
        return 'Some systems degraded';
    }
    if (category === 'critical') {
        return 'Critical incidents detected';
    }
    return 'No data';
};

const deriveSystemStatusCategory = (executiveData: any): WorldMapHealthCategory => {
    const healthLabel = String(executiveData?.metadata?.healthScoreLabel ?? '').toLowerCase();
    if (healthLabel.includes('critical')) {
        return 'critical';
    }
    if (healthLabel.includes('warning') || healthLabel.includes('risk')) {
        return 'warning';
    }
    if (healthLabel.includes('healthy') || healthLabel.includes('good')) {
        return 'healthy';
    }
    if (Number(executiveData?.volume?.totalProcessed ?? 0) <= 0) {
        return 'noData';
    }
    return 'healthy';
};

const deriveCountryHealthCategory = (summary: {
    totalProcessed: number;
    errorRate: number;
    inTransitCount: number;
    successRate: number;
}): WorldMapHealthCategory => {
    const total = summary.totalProcessed;
    if (total <= 0) {
        return 'noData';
    }

    const failureRate = summary.errorRate;
    const inTransitRate = total > 0 ? (summary.inTransitCount * 100) / total : 0;
    const successRate = summary.successRate;

    if (failureRate >= inTransitRate && failureRate >= successRate) {
        return 'critical';
    }

    if (inTransitRate >= failureRate && inTransitRate >= successRate) {
        return 'warning';
    }

    return 'healthy';
};

const getTrendArrowIcon = (showTrendArrow: boolean | undefined, trendTone: TrendTone, className: string) => {
    if (!showTrendArrow) {
        return null;
    }

    if (trendTone === 'positive') {
        return <ArrowUpOutlined className={className} />;
    }

    return <ArrowDownOutlined className={className} />;
};

const getCountryErrorStrokeColor = (tone: string): string => {
    if (tone === 'critical') {
        return '#ef4444';
    }

    if (tone === 'warning') {
        return '#f5b436';
    }

    if (tone === 'healthy') {
        return '#22c55e';
    }

    return '#9db0ce';
};

const getMainGridClassName = (stylesRef: Record<string, string>): string => stylesRef.mainGrid;

const getMapAreaClassName = (stylesRef: Record<string, string>): string => {
    return `${stylesRef.mapArea} ${stylesRef.mapAreaExpanded}`;
};

const getPageClassName = (stylesRef: Record<string, string>, theme: ThemeMode): string => {
    if (theme === 'light') {
        return `${stylesRef.page} ${stylesRef.pageLight}`;
    }

    return `${stylesRef.page} ${stylesRef.pageDark}`;
};

const getDashboardShellClassName = (stylesRef: Record<string, string>, theme: ThemeMode): string => {
    if (theme === 'light') {
        return `${stylesRef.dashboardShell} ${stylesRef.themeLight}`;
    }

    return stylesRef.dashboardShell;
};

const getCountryMetricTotalProcessed = (metrics: CountryMetric[]) => metrics.reduce((accumulator, metric) => (
    accumulator + Number(metric.intake?.totalProcessed ?? 0)
), 0);

const getInvoiceDeltaPercent = (todayTotal: number, yesterdayTotal: number) => {
    if (yesterdayTotal <= 0) {
        return todayTotal > 0 ? 100 : 0;
    }

    return ((todayTotal - yesterdayTotal) / yesterdayTotal) * 100;
};

const formatPercent = (value: number, intl: IAppComponentProps['intl']) => `${intl.formatNumber(Number.isFinite(value) ? value : 0, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
})}%`;

const formatCount = (value: number, intl: IAppComponentProps['intl']) => intl.formatNumber(Number.isFinite(value) ? value : 0);

const getTodayHourLabels = () => {
    const currentHour = dayjs().hour();
    return new Array<string>(currentHour + 1).fill('').map((_, hour) => `${String(hour).padStart(2, '0')}:00`);
};

const normalizeHourlyBucketLabel = (value: unknown): string | null => {
    let raw = '';
    if (typeof value === 'string') {
        raw = value.trim();
    } else if (typeof value === 'number' && Number.isFinite(value)) {
        raw = String(value);
    } else if (value instanceof Date) {
        raw = value.toISOString();
    }

    if (!raw) {
        return null;
    }

    const simpleHourPattern = /^(\d{1,2})(?::\d{2})?$/;
    const simpleHourMatch = simpleHourPattern.exec(raw);
    if (simpleHourMatch) {
        const hourValue = Number.parseInt(simpleHourMatch[1], 10);
        if (!Number.isNaN(hourValue) && hourValue >= 0 && hourValue <= 23) {
            return `${String(hourValue).padStart(2, '0')}:00`;
        }
    }

    const embeddedHourPattern = /(?:T|\s)(\d{1,2}):\d{2}/;
    const embeddedHourMatch = embeddedHourPattern.exec(raw);
    if (embeddedHourMatch) {
        const hourValue = Number.parseInt(embeddedHourMatch[1], 10);
        if (!Number.isNaN(hourValue) && hourValue >= 0 && hourValue <= 23) {
            return `${String(hourValue).padStart(2, '0')}:00`;
        }
    }

    const parsed = dayjs(raw);
    if (parsed.isValid()) {
        return parsed.format('HH:00');
    }

    return null;
};

const buildInvoiceSparklineSeries = (rows: HourlyTrendRow[]) => {
    const labels = getTodayHourLabels();
    const totalsByHour = new Map<string, number>(labels.map((label) => [label, 0]));

    rows.forEach((row) => {
        const normalizedLabel = normalizeHourlyBucketLabel(row.hourLabel);
        if (!normalizedLabel || !totalsByHour.has(normalizedLabel)) {
            return;
        }

        const total = Number(row.successfulCount ?? 0) + Number(row.failedCount ?? 0);
        totalsByHour.set(normalizedLabel, (totalsByHour.get(normalizedLabel) ?? 0) + Math.max(0, total));
    });

    return labels.map((label) => totalsByHour.get(label) ?? 0);
};

const buildOpenIncidentBarsSeries = (rows: HourlyTrendRow[]) => {
    const labels = getTodayHourLabels();
    const failedByHour = new Map<string, number>(labels.map((label) => [label, 0]));

    rows.forEach((row) => {
        const normalizedLabel = normalizeHourlyBucketLabel(row.hourLabel);
        if (!normalizedLabel || !failedByHour.has(normalizedLabel)) {
            return;
        }

        const failedCount = Math.max(0, Number(row.failedCount ?? 0));
        failedByHour.set(normalizedLabel, (failedByHour.get(normalizedLabel) ?? 0) + failedCount);
    });

    return labels.map((label) => failedByHour.get(label) ?? 0).slice(-12);
};

const formatProcessingTime = (days: number, intl: any): string => {
    if (days < 1) {
        const hours = Math.round(days * 24 * 10) / 10;
        return `${intl.formatNumber(hours, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} h`;
    }
    return `${intl.formatNumber(days, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} d`;
};

const buildAvgProcessingLineSeries = (metrics: CountryMetric[]) => {
    const values = metrics
        .map((metric) => Number(metric?.processing?.avgTransitDays ?? 0))
        .filter((value) => Number.isFinite(value));

    if (values.length <= 12) {
        return values;
    }

    return values.slice(-12);
};

const buildCountryMetricsAggregate = (metrics: CountryMetric[]) => {
    const totals = metrics.reduce((accumulator, metric) => {
        const processedCount = Math.max(0, Number(metric.intake?.totalProcessed ?? 0));
        const inTransitCount = Math.max(0, Number(metric.processing?.inTransitCount ?? 0));
        const onTimeRate = Math.max(0, Number(metric.processing?.onTimeRate ?? 0));
        const failedCount = Math.max(0, Number(metric.exceptions?.failedCount ?? 0));
        const overdueCount = Math.max(0, Number(metric.exceptions?.overdueCount ?? 0));
        const avgTransitDays = Math.max(0, Number(metric.processing?.avgTransitDays ?? 0));

        accumulator.totalProcessed += processedCount;
        accumulator.inTransitCount += inTransitCount;
        accumulator.totalOnTimeRate += onTimeRate * processedCount;
        accumulator.failedCount += failedCount;
        accumulator.overdueCount += overdueCount;
        accumulator.totalTransitDays += avgTransitDays * processedCount;

        return accumulator;
    }, {
        totalProcessed: 0,
        inTransitCount: 0,
        totalOnTimeRate: 0,
        failedCount: 0,
        overdueCount: 0,
        totalTransitDays: 0,
    });

    const errorCount = totals.failedCount + totals.overdueCount;
    const successCount = Math.max(0, totals.totalProcessed - totals.inTransitCount - errorCount);
    const successRate = totals.totalProcessed > 0 ? (successCount * 100) / totals.totalProcessed : 0;
    const onTimeRate = totals.totalProcessed > 0 ? totals.totalOnTimeRate / totals.totalProcessed : 0;
    const avgTransitDays = totals.totalProcessed > 0 ? totals.totalTransitDays / totals.totalProcessed : 0;

    return {
        ...totals,
        successCount,
        successRate,
        onTimeRate,
        avgTransitDays,
    };
};

type CountrySummary = {
    totalProcessed: number;
    inTransitCount: number;
    failedCount: number;
    overdueCount: number;
    successCount: number;
    successRate: number;
    errorRate: number;
    stpRate: number;
    avgTransitDays: number;
};

type CountryMetricChanges = {
    yesterdayInvoiceTotal: number;
    invoicesDeltaPercent: number;
    successRateDelta: number;
    avgTransitDelta: number;
    errorRateDelta: number;
};

const buildSelectedCountrySummary = (
    selectedMetrics: CountryMetric[],
    countryDetailMetrics: CountryDetailMetrics | null,
    hasCountrySelection: boolean
): CountrySummary => {
    if (countryDetailMetrics && hasCountrySelection) {
        const totalProcessed = Number(countryDetailMetrics.totalProcessed ?? 0);
        const inTransitCount = Number(countryDetailMetrics.inTransitCount ?? 0);
        const failedCount = Number(countryDetailMetrics.failedCount ?? 0);
        const overdueCount = Number(countryDetailMetrics.overdueCount ?? 0);
        const successCount = Number(countryDetailMetrics.successfulCount ?? 0);
        const errorCount = failedCount + overdueCount;
        const stpRate = Number(countryDetailMetrics.stpRate ?? 0);
        const errorRate = totalProcessed > 0 ? (errorCount * 100) / totalProcessed : 0;
        const successRate = Number.isFinite(stpRate) && stpRate > 0
            ? stpRate
            : Math.max(0, 100 - errorRate);

        return {
            totalProcessed,
            inTransitCount,
            failedCount,
            overdueCount,
            successCount,
            successRate,
            errorRate,
            stpRate,
            avgTransitDays: Number(countryDetailMetrics.avgTransitDays ?? 0),
        };
    }

    const totals = selectedMetrics.reduce((accumulator, metric) => {
        accumulator.totalProcessed += Number(metric.intake?.totalProcessed ?? 0);
        accumulator.inTransitCount += Number(metric.processing?.inTransitCount ?? 0);
        accumulator.failedCount += Number(metric.exceptions?.failedCount ?? 0);
        accumulator.overdueCount += Number(metric.exceptions?.overdueCount ?? 0);
        accumulator.stpRate += Number(metric.processing?.stpRate ?? 0);
        accumulator.avgTransitDays += Number(metric.processing?.avgTransitDays ?? 0);
        return accumulator;
    }, {
        totalProcessed: 0,
        inTransitCount: 0,
        failedCount: 0,
        overdueCount: 0,
        stpRate: 0,
        avgTransitDays: 0,
    });

    const rowCount = selectedMetrics.length || 1;
    const stpRate = totals.stpRate / rowCount;
    const avgTransitDays = totals.avgTransitDays / rowCount;
    const errorCount = totals.failedCount + totals.overdueCount;
    const successCount = Math.max(0, totals.totalProcessed - totals.inTransitCount - errorCount);
    const errorRate = totals.totalProcessed > 0 ? (errorCount * 100) / totals.totalProcessed : 0;
    const successRate = Number.isFinite(stpRate) && stpRate > 0
        ? stpRate
        : Math.max(0, 100 - errorRate);

    return {
        totalProcessed: totals.totalProcessed,
        inTransitCount: totals.inTransitCount,
        failedCount: totals.failedCount,
        overdueCount: totals.overdueCount,
        successCount,
        successRate,
        errorRate,
        stpRate,
        avgTransitDays,
    };
};

const buildCountryMetricChanges = (
    selectedTodayMetrics: CountryMetric[],
    selectedYesterdayMetrics: CountryMetric[]
): CountryMetricChanges => {
    const todayAggregate = buildCountryMetricsAggregate(selectedTodayMetrics);
    const yesterdayAggregate = buildCountryMetricsAggregate(selectedYesterdayMetrics);
    const successRateDelta = todayAggregate.successRate - yesterdayAggregate.successRate;
    const avgTransitDelta = todayAggregate.avgTransitDays - yesterdayAggregate.avgTransitDays;
    const todayErrorRate = todayAggregate.totalProcessed > 0
        ? ((todayAggregate.failedCount + todayAggregate.overdueCount) * 100) / todayAggregate.totalProcessed
        : 0;
    const yesterdayErrorRate = yesterdayAggregate.totalProcessed > 0
        ? ((yesterdayAggregate.failedCount + yesterdayAggregate.overdueCount) * 100) / yesterdayAggregate.totalProcessed
        : 0;
    const errorRateDelta = todayErrorRate - yesterdayErrorRate;
    const invoicesDeltaPercent = getInvoiceDeltaPercent(todayAggregate.totalProcessed, yesterdayAggregate.totalProcessed);

    return {
        yesterdayInvoiceTotal: yesterdayAggregate.totalProcessed,
        invoicesDeltaPercent,
        successRateDelta,
        avgTransitDelta,
        errorRateDelta,
    };
};

const buildTopMetrics = ({
    intl,
    periodDisplayLabel,
    comparisonPeriodDisplayLabel,
    trendComparisonSuffix,
    selectedTodayMetrics,
    selectedYesterdayMetrics,
    selectedTodayAggregate,
    selectedYesterdayAggregate,
    globalInvoiceTrendRows,
    onMetricClick,
}: {
    intl: IAppComponentProps['intl'];
    periodDisplayLabel: string;
    comparisonPeriodDisplayLabel: string;
    trendComparisonSuffix: string;
    selectedTodayMetrics: CountryMetric[];
    selectedYesterdayMetrics: CountryMetric[];
    selectedTodayAggregate: ReturnType<typeof buildCountryMetricsAggregate>;
    selectedYesterdayAggregate: ReturnType<typeof buildCountryMetricsAggregate>;
    globalInvoiceTrendRows: HourlyTrendRow[];
    onMetricClick?: (options: { type?: 'IN' | 'OUT'; statusFilters?: string[] }) => void;
}): TopMetricDefinition[] => {
    const invoiceTodayTotal = getCountryMetricTotalProcessed(selectedTodayMetrics);
    const inboundTodayTotal = selectedTodayMetrics.reduce((sum, countryMetric) => (
        sum + Math.max(0, Number(countryMetric.intake?.inboundCount ?? 0))
    ), 0);
    const outboundTodayTotal = selectedTodayMetrics.reduce((sum, countryMetric) => (
        sum + Math.max(0, Number(countryMetric.intake?.outboundCount ?? 0))
    ), 0);
    const inboundShare = invoiceTodayTotal > 0 ? (inboundTodayTotal * 100) / invoiceTodayTotal : 0;
    const outboundShare = invoiceTodayTotal > 0 ? (outboundTodayTotal * 100) / invoiceTodayTotal : 0;
    const successRateDelta = selectedTodayAggregate.successRate - selectedYesterdayAggregate.successRate;
    const avgProcessingDelta = selectedTodayAggregate.avgTransitDays - selectedYesterdayAggregate.avgTransitDays;
    const incidentTodayCount = selectedTodayAggregate.failedCount + selectedTodayAggregate.overdueCount;
    const incidentYesterdayCount = selectedYesterdayAggregate.failedCount + selectedYesterdayAggregate.overdueCount;
    const incidentDeltaPercent = getInvoiceDeltaPercent(incidentTodayCount, incidentYesterdayCount);
    const overdueCurrentCount = selectedTodayAggregate.overdueCount ?? 0;
    const overduePreviousCount = selectedYesterdayAggregate.overdueCount ?? 0;
    const overdueDeltaPercent = getInvoiceDeltaPercent(overdueCurrentCount, overduePreviousCount);
    const overdueComparisonBarData = [overdueCurrentCount, overduePreviousCount];
    const inTransitCurrentCount = selectedTodayAggregate.inTransitCount ?? 0;
    const inTransitPreviousCount = selectedYesterdayAggregate.inTransitCount ?? 0;
    const inTransitDeltaPercent = getInvoiceDeltaPercent(inTransitCurrentCount, inTransitPreviousCount);
    const avgProcessingLineData = buildAvgProcessingLineSeries(selectedTodayMetrics);
    const openIncidentBarData = buildOpenIncidentBarsSeries(globalInvoiceTrendRows);
    const inTransitComparisonBarData = [inTransitCurrentCount, inTransitPreviousCount];

    const currentActiveCountries = new Set(selectedTodayMetrics
        .filter((countryMetric) => Number(countryMetric.intake?.totalProcessed ?? 0) > 0)
        .map((countryMetric) => (countryMetric.country ?? '').trim().toUpperCase())
        .filter(Boolean));
    const previousActiveCountries = new Set(selectedYesterdayMetrics
        .filter((countryMetric) => Number(countryMetric.intake?.totalProcessed ?? 0) > 0)
        .map((countryMetric) => (countryMetric.country ?? '').trim().toUpperCase())
        .filter(Boolean));
    const totalCurrentCountries = currentActiveCountries.size;
    const totalPreviousCountries = previousActiveCountries.size;
    const activeCountriesDeltaPercent = getInvoiceDeltaPercent(totalCurrentCountries, totalPreviousCountries);

    return [
        {
            key: 'eim-v2d-kpi-invoices-period',
            infoKey: 'eim-v2d-kpi-info-invoices-period',
            value: () => formatCount(invoiceTodayTotal, intl),
            trend: () => periodDisplayLabel,
            trendTone: 'positive',
            showTrendArrow: false,
            isClickable: !!onMetricClick,
            onClick: () => onMetricClick?.({}),
        },
        {
            key: 'eim-v2d-kpi-inbound-vs-outbound',
            infoKey: 'eim-v2d-kpi-info-inbound-vs-outbound',
            value: () => `${formatCount(inboundTodayTotal, intl)} / ${formatCount(outboundTodayTotal, intl)}`,
            trend: () => `${formatPercent(inboundShare, intl)} / ${formatPercent(outboundShare, intl)}`,
            trendTone: 'positive',
            showTrendArrow: false,
            miniVisualKind: 'progress',
            miniVisualValue: inboundShare,
            isClickable: !!onMetricClick,
            onClick: () => onMetricClick?.({ type: 'IN' }),
        },
        {
            key: 'eim-v2d-kpi-success-rate',
            infoKey: 'eim-v2d-kpi-info-success-rate',
            value: () => formatPercent(selectedTodayAggregate.successRate, intl),
            trend: () => `${intl.formatNumber(successRateDelta, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
                signDisplay: 'always' as any,
            })}% ${trendComparisonSuffix}`,
            trendTone: successRateDelta >= 0 ? 'positive' : 'negative',
            showTrendArrow: true,
            miniVisualKind: 'ring',
            miniVisualValue: selectedTodayAggregate.successRate,
            isClickable: !!onMetricClick,
            onClick: () => onMetricClick?.({}),
        },
        {
            key: 'eim-v2d-kpi-avg-processing-time',
            infoKey: 'eim-v2d-kpi-info-avg-processing-time',
            value: () => formatProcessingTime(selectedTodayAggregate.avgTransitDays, intl),
            trend: () => {
                const deltaDisplay = avgProcessingDelta < 1 && avgProcessingDelta > -1
                    ? `${intl.formatNumber(avgProcessingDelta * 24, {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                        signDisplay: 'always' as any,
                    })} h`
                    : `${intl.formatNumber(avgProcessingDelta, {
                        minimumFractionDigits: 1,
                        maximumFractionDigits: 1,
                        signDisplay: 'always' as any,
                    })} d`;
                return `${deltaDisplay} ${trendComparisonSuffix}`;
            },
            trendTone: avgProcessingDelta <= 0 ? 'positive' : 'negative',
            showTrendArrow: true,
            miniVisualKind: 'line',
            miniVisualData: avgProcessingLineData,
            isClickable: !!onMetricClick,
            onClick: () => onMetricClick?.({}),
        },
        {
            key: 'eim-v2d-kpi-active-countries',
            infoKey: 'eim-v2d-kpi-info-active-countries',
            value: () => `${formatCount(totalCurrentCountries, intl)} / ${formatCount(totalPreviousCountries, intl)}`,
            trend: () => `${intl.formatNumber(activeCountriesDeltaPercent, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
                signDisplay: 'always' as any,
            })}% ${trendComparisonSuffix}`,
            hint: () => `${periodDisplayLabel} / ${comparisonPeriodDisplayLabel} · ${getI18n('eim-v2d-active-countries-note', 'Distinct countries with invoices in each period', intl)}`,
            trendTone: activeCountriesDeltaPercent >= 0 ? 'positive' : 'negative',
            isClickable: !!onMetricClick,
            onClick: () => onMetricClick?.({}),
        },
        {
            key: 'eim-v2d-kpi-open-incidents',
            infoKey: 'eim-v2d-kpi-info-open-errors',
            value: () => formatCount(incidentTodayCount, intl),
            trend: () => `${intl.formatNumber(incidentDeltaPercent, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
                signDisplay: 'always' as any,
            })}% ${trendComparisonSuffix} (${formatCount(incidentYesterdayCount, intl)})`,
            trendTone: incidentDeltaPercent <= 0 ? 'positive' : 'negative',
            showTrendArrow: true,
            miniVisualKind: 'bars',
            miniVisualData: openIncidentBarData,
            isClickable: !!onMetricClick,
            onClick: () => onMetricClick?.({ statusFilters: ['FAIL'] }),
        },
        {
            key: 'eim-v2d-kpi-in-transit',
            infoKey: 'eim-v2d-kpi-info-in-transit',
            value: () => `${formatCount(inTransitCurrentCount, intl)} / ${formatCount(inTransitPreviousCount, intl)}`,
            trend: () => `${intl.formatNumber(inTransitDeltaPercent, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
                signDisplay: 'always' as any,
            })}% ${trendComparisonSuffix}`,
            hint: () => `${periodDisplayLabel} / ${comparisonPeriodDisplayLabel} · ${getI18n('eim-v2d-in-transit-note', 'Distinct invoices currently in transit', intl)}`,
            trendTone: inTransitDeltaPercent <= 0 ? 'positive' : 'negative',
            showTrendArrow: true,
            miniVisualKind: 'bars',
            miniVisualData: inTransitComparisonBarData,
            isClickable: !!onMetricClick,
            onClick: () => onMetricClick?.({ statusFilters: ['INTRANSIT'] }),
        },
        {
            key: 'eim-v2d-kpi-overdue',
            infoKey: 'eim-v2d-kpi-info-overdue',
            value: () => `${formatCount(overdueCurrentCount, intl)} / ${formatCount(overduePreviousCount, intl)}`,
            trend: () => `${intl.formatNumber(overdueDeltaPercent, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
                signDisplay: 'always' as any,
            })}% ${trendComparisonSuffix}`,
            hint: () => `${periodDisplayLabel} / ${comparisonPeriodDisplayLabel} · ${getI18n('eim-v2d-overdue-note', 'Distinct invoices currently overdue', intl)}`,
            trendTone: overdueDeltaPercent <= 0 ? 'positive' : 'negative',
            showTrendArrow: true,
            miniVisualKind: 'bars',
            miniVisualData: overdueComparisonBarData,
            isClickable: !!onMetricClick,
            onClick: () => onMetricClick?.({ statusFilters: ['OVERDUE'] }),
        },
    ];
};

const getSparklinePolylinePoints = (values: number[], width: number, height: number) => {
    if (values.length < 2) {
        return '';
    }

    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const safeRange = Math.max(1, maxValue - minValue);

    return values.map((value, index) => {
        const x = (index / Math.max(1, values.length - 1)) * width;
        const normalized = (value - minValue) / safeRange;
        const y = height - (normalized * height);
        return `${x},${y}`;
    }).join(' ');
};

const OPERATIONAL_DATA_ORIGIN_KEY = 'eimOperationalDataOrigin';
const EXECUTIVE_DASHBOARD_V2_ROUTE = '/eim-document-executive-dashboard-monitoring-v2';

const getSortDirectionIcon = (isActive: boolean, direction: CountryVolumeSortDirection) => {
    if (!isActive) {
        return null;
    }

    if (direction === 'asc') {
        return <ArrowUpOutlined />;
    }

    return <ArrowDownOutlined />;
};

const ExecutiveV2BottomDeck: FunctionComponent<{
    intl: IAppComponentProps['intl'];
    navigate: ReturnType<typeof useNavigate>;
    theme: ThemeMode;
    systemStatusCategory: WorldMapHealthCategory;
    systemStatusLabel: string;
    incidentRows: IncidentRow[];
    leaderboardRows: VolumeLeaderboardRow[];
    allLeaderboardRows: VolumeLeaderboardRow[];
    leaderboardTitle: string;
    leaderboardModalTitle: string;
    leaderboardViewAllLabel: string;
    leaderboardNameColumnLabel: string;
    leaderboardNameSortLabel: string;
    groupingRows: VolumeLeaderboardRow[];
    allGroupingRows: VolumeLeaderboardRow[];
    groupingTitle: string;
    groupingModalTitle: string;
    groupingViewAllLabel: string;
    isCountrySelected: boolean;
    selectedCountryCode: string;
    selectableCountryOptions: Array<{ value: string; label: string }>;
    onSelectCountry: (countryCode: string) => void;
    onViewErrors: () => void;
}> = ({
    intl,
    navigate,
    theme,
    systemStatusCategory,
    systemStatusLabel,
    incidentRows,
    leaderboardRows,
    allLeaderboardRows,
    leaderboardTitle,
    leaderboardModalTitle,
    leaderboardViewAllLabel,
    leaderboardNameColumnLabel,
    leaderboardNameSortLabel,
    groupingRows,
    allGroupingRows,
    groupingTitle,
    groupingModalTitle,
    groupingViewAllLabel,
    isCountrySelected,
    selectedCountryCode,
    selectableCountryOptions,
    onSelectCountry,
    onViewErrors,
}) => {
    const [countryVolumeModalOpen, setCountryVolumeModalOpen] = useState(false);
    const [groupingModalOpen, setGroupingModalOpen] = useState(false);
    const [countryVolumeSortKey, setCountryVolumeSortKey] = useState<CountryVolumeSortKey>('volume');
    const [countryVolumeSortDirection, setCountryVolumeSortDirection] = useState<CountryVolumeSortDirection>('desc');

    const handleCountrySort = (sortKey: CountryVolumeSortKey) => {
        if (countryVolumeSortKey === sortKey) {
            setCountryVolumeSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
            return;
        }

        setCountryVolumeSortKey(sortKey);
        setCountryVolumeSortDirection(sortKey === 'name' ? 'asc' : 'desc');
    };

    const aggregatedCountryVolumeRows = useMemo(() => {
        const rowsByEntry = new Map<string, VolumeLeaderboardRow>();
        let unmappedTotal = 0;
        let unmappedErrors = 0;

        allLeaderboardRows.forEach((row) => {
            const normalizedCode = (row.code ?? '').trim().toUpperCase();
            const normalizedName = (row.name ?? '').trim();
            const key = normalizedCode || normalizedName;
            
            if (!key) {
                // Track unmapped/blank countries separately
                unmappedTotal += Number(row.totalProcessed ?? 0);
                unmappedErrors += Number(row.totalErrors ?? 0);
                return;
            }

            const existingRow = rowsByEntry.get(key);
            if (!existingRow) {
                rowsByEntry.set(key, {
                    ...row,
                    code: normalizedCode || key,
                    name: normalizedName || key,
                    totalProcessed: Number(row.totalProcessed ?? 0),
                    totalErrors: Number(row.totalErrors ?? 0),
                    errorRate: Number(row.errorRate ?? 0),
                });
                return;
            }

            existingRow.totalProcessed += Number(row.totalProcessed ?? 0);
            existingRow.totalErrors += Number(row.totalErrors ?? 0);
            const safeTotal = Math.max(0, existingRow.totalProcessed);
            existingRow.errorRate = safeTotal > 0 ? (existingRow.totalErrors * 100) / safeTotal : 0;
            if (!existingRow.name && normalizedName) {
                existingRow.name = normalizedName;
            }
        });

        const result = Array.from(rowsByEntry.values());

        // Add unmapped row if there are unmapped entries
        if (unmappedTotal > 0) {
            result.push({
                code: 'UNMAPPED',
                name: getI18n('eim-unmapped-countries-label', 'Unmapped', intl),
                totalProcessed: unmappedTotal,
                totalErrors: unmappedErrors,
                errorRate: unmappedTotal > 0 ? (unmappedErrors * 100) / unmappedTotal : 0,
                rank: 0,
                countryCode: undefined,
                progress: 0,
            });
        }

        return result;
    }, [allLeaderboardRows, intl]);

    const rankedCountryVolumeRows = useMemo(() => {
        return [...aggregatedCountryVolumeRows]
            .sort((left, right) => {
                const volumeCompare = right.totalProcessed - left.totalProcessed;
                if (volumeCompare !== 0) {
                    return volumeCompare;
                }

                return left.name.localeCompare(right.name);
            })
            .map((row, index) => ({
                ...row,
                rank: index + 1,
            }));
    }, [aggregatedCountryVolumeRows]);

    const sortedCountryVolumeRows = useMemo(() => {
        const totalVolume = rankedCountryVolumeRows.reduce((sum, row) => sum + row.totalProcessed, 0);
        const rows = [...rankedCountryVolumeRows].sort((left, right) => {
            if (countryVolumeSortKey === 'name') {
                const nameCompare = left.name.localeCompare(right.name);
                return countryVolumeSortDirection === 'asc' ? nameCompare : -nameCompare;
            }

            const volumeCompare = left.totalProcessed - right.totalProcessed;
            return countryVolumeSortDirection === 'asc' ? volumeCompare : -volumeCompare;
        });

        return rows.map((row) => ({
            ...row,
            sharePercent: totalVolume > 0 ? (row.totalProcessed / totalVolume) * 100 : 0,
        }));
    }, [countryVolumeSortDirection, countryVolumeSortKey, rankedCountryVolumeRows]);

    const countryVolumeTotals = useMemo(() => {
        const totalVolume = sortedCountryVolumeRows.reduce((sum, row) => sum + row.totalProcessed, 0);
        const totalErrors = sortedCountryVolumeRows.reduce((sum, row) => sum + row.totalErrors, 0);
        const avgErrorRate = totalVolume > 0 ? (totalErrors / totalVolume) * 100 : 0;

        return { totalVolume, totalErrors, avgErrorRate };
    }, [sortedCountryVolumeRows]);

    const groupingRowsWithShare = useMemo(() => {
        const totalVolume = allGroupingRows.reduce((sum, row) => sum + row.totalProcessed, 0);
        return allGroupingRows.map((row) => ({
            ...row,
            sharePercent: totalVolume > 0 ? (row.totalProcessed / totalVolume) * 100 : 0,
        }));
    }, [allGroupingRows]);

    const groupingTotals = useMemo(() => {
        const totalVolume = allGroupingRows.reduce((sum, row) => sum + row.totalProcessed, 0);
        const totalErrors = allGroupingRows.reduce((sum, row) => sum + row.totalErrors, 0);
        const avgErrorRate = totalVolume > 0 ? (totalErrors / totalVolume) * 100 : 0;

        return { totalVolume, totalErrors, avgErrorRate };
    }, [allGroupingRows]);

    const deckCountryOptions = useMemo(
        () => [
            {
                value: '',
                label: getI18n('eim-v2d-all-countries', 'All countries', intl),
            },
            ...selectableCountryOptions,
        ],
        [intl, selectableCountryOptions]
    );

    return (
    <>
    <section className={styles.deckGrid}>
        <Card className={styles.deckCard} variant="borderless">
            <div className={styles.deckHeader}>
                <h3>{getI18n('eim-v2d-system-status-title', 'System Status', intl)}</h3>
            </div>
            <div
                className={[
                    styles.systemStatusChip,
                    systemStatusCategory === 'healthy' ? styles.systemStatusHealthy : '',
                    systemStatusCategory === 'warning' ? styles.systemStatusWarning : '',
                    systemStatusCategory === 'critical' ? styles.systemStatusCritical : '',
                    systemStatusCategory === 'noData' ? styles.systemStatusNoData : '',
                ].join(' ').trim()}
            >
                {systemStatusLabel}
            </div>
            <div className={styles.deckMeta}>{getI18n('eim-v2d-last-updated', 'Last update', intl)}: {dayjs().format('HH:mm:ss')} UTC</div>
            <button className={styles.deckLinkButton} onClick={() => navigate('/eim-document-executive-dashboard-monitoring')}>
                {getI18n('eim-v2d-view-status-details', 'View status details', intl)} <ArrowRightOutlined />
            </button>
        </Card>

        <Card className={styles.deckCard} variant="borderless">
            <div className={styles.deckHeader}>
                <h3>
                    <span>{getI18n('eim-v2d-incident-feed-title', 'Error Feed - All Countries', intl)}</span>
                    <Tooltip
                        title={(
                            <div>
                                <div><strong>{getI18n('eim-v2d-error-level-critical', 'Critical', intl)}:</strong> {getI18n('eim-v2d-error-level-critical-description', 'Highest-priority error condition requiring immediate attention.', intl)}</div>
                                <div><strong>{getI18n('eim-v2d-error-level-warning', 'Warning', intl)}:</strong> {getI18n('eim-v2d-error-level-warning-description', 'Error condition requiring attention, but with lower urgency than Critical.', intl)}</div>
                            </div>
                        )}
                        placement="topLeft"
                    >
                        <Button
                            type="link"
                            size="small"
                            icon={<InfoCircleOutlined />}
                            aria-label={getI18n('eim-v2d-error-level-info', 'Explain error severity levels', intl)}
                        />
                    </Tooltip>
                </h3>
                <button className={styles.deckLinkButton} onClick={onViewErrors}>
                    {getI18n('eim-v2d-view-all-incidents', 'View all errors', intl)} <ArrowRightOutlined />
                </button>
            </div>
            <ul className={styles.incidentList}>
                {incidentRows.map((incident) => (
                    <li key={incident.key}>
                        <span className={incident.severity === 'critical' ? styles.severityCritical : styles.severityWarning}>
                            {incident.severity === 'critical'
                                ? getI18n('eim-world-map-health-critical', 'Critical', intl)
                                : getI18n('eim-world-map-health-warning', 'Warning', intl)}
                        </span>
                        <div>
                            <Tooltip title={incident.label} placement="topLeft">
                                <strong className={styles.incidentLabel}>{incident.label}</strong>
                            </Tooltip>
                            <span className={styles.incidentDetail}>{incident.detail}</span>
                        </div>
                    </li>
                ))}
                {incidentRows.length === 0 ? <li>{getI18n('eim-v2d-no-incident-data', 'No incident data available.', intl)}</li> : null}
            </ul>
        </Card>

        <Card className={classNames(styles.deckCard, styles.leaderboardDeckCard)} variant="borderless">
            <div className={styles.deckHeader}>
                <h3>{leaderboardTitle}</h3>
                <div className={styles.deckHeaderActions}>
                    <Select
                        size="small"
                        showSearch={true}
                        className={styles.deckCountrySelect}
                        value={selectedCountryCode}
                        placeholder={getI18n('eim-v2d-select-country-placeholder', 'Select country', intl)}
                        options={deckCountryOptions}
                        optionFilterProp="label"
                        onChange={(value) => onSelectCountry(String(value ?? ''))}
                    />
                    <button className={styles.deckLinkButton} onClick={() => setCountryVolumeModalOpen(true)}>
                        {leaderboardViewAllLabel} <ArrowRightOutlined />
                    </button>
                </div>
            </div>
            <div className={styles.countryVolumeHeader}>
                <span>{getI18n('eim-v2d-countries-col-rank', '#', intl)}</span>
                <span>{leaderboardNameColumnLabel}</span>
                <span>{getI18n('eim-v2d-countries-col-volume', 'Volume', intl)}</span>
                <span>{getI18n('eim-v2d-countries-col-total-errors', 'Total errors', intl)}</span>
            </div>
            <ul className={styles.countryVolumeList}>
                {leaderboardRows.map((row) => (
                    <li key={`${row.code}-${row.rank}`}>
                        {row.countryCode ? (
                            <button
                                type="button"
                                className={styles.countryVolumeListRowSelectable}
                                onClick={() => onSelectCountry(row.countryCode ?? '')}
                            >
                                <span className={styles.rankBadge}>{row.rank}</span>
                                <div className={styles.countryVolumeNameCell}>
                                    <CountryFlagLabel
                                        countryCode={row.countryCode}
                                        label={row.name}
                                        className={styles.countryName}
                                    />
                                    <progress className={styles.progressBar} value={row.progress} max={100} />
                                </div>
                                <strong className={styles.countryVolumeMetric}>{intl.formatNumber(row.totalProcessed)}</strong>
                                <strong className={styles.countryVolumeMetric}>{intl.formatNumber(row.totalErrors)}</strong>
                            </button>
                        ) : (
                            <div className={styles.countryVolumeListRowStatic}>
                                <span className={styles.rankBadge}>{row.rank}</span>
                                <div className={styles.countryVolumeNameCell}>
                                    <span className={styles.countryName}>{row.name}</span>
                                    <progress className={styles.progressBar} value={row.progress} max={100} />
                                </div>
                                <strong className={styles.countryVolumeMetric}>{intl.formatNumber(row.totalProcessed)}</strong>
                                <strong className={styles.countryVolumeMetric}>{intl.formatNumber(row.totalErrors)}</strong>
                            </div>
                        )}
                    </li>
                ))}
                {leaderboardRows.length === 0 ? <li>{getI18n('eim-v2d-no-country-volume-data', 'No country volume data available.', intl)}</li> : null}
            </ul>
        </Card>

        <Card className={styles.deckCard} variant="borderless">
            <div className={styles.deckHeader}>
                <h3>{groupingTitle}</h3>
                <button className={styles.deckLinkButton} onClick={() => setGroupingModalOpen(true)}>
                    {groupingViewAllLabel} <ArrowRightOutlined />
                </button>
            </div>
            <div className={styles.countryVolumeHeader}>
                <span>{getI18n('eim-v2d-countries-col-rank', '#', intl)}</span>
                <span>{getI18n('eim-v2d-countries-col-grouping', 'BMW Grouping', intl)}</span>
                <span>{getI18n('eim-v2d-countries-col-volume', 'Volume', intl)}</span>
                <span>{getI18n('eim-v2d-countries-col-total-errors', 'Total errors', intl)}</span>
            </div>
            <ul className={styles.countryVolumeList}>
                {groupingRows.map((row) => (
                    <li key={`${row.code}-${row.rank}`}>
                        <div className={styles.countryVolumeListRowStatic}>
                            <span className={styles.rankBadge}>{row.rank}</span>
                            <div className={styles.countryVolumeNameCell}>
                                <span className={styles.countryName}>{row.name}</span>
                                <progress className={styles.progressBar} value={row.progress} max={100} />
                            </div>
                            <strong className={styles.countryVolumeMetric}>{intl.formatNumber(row.totalProcessed)}</strong>
                            <strong className={styles.countryVolumeMetric}>{intl.formatNumber(row.totalErrors)}</strong>
                        </div>
                    </li>
                ))}
                {groupingRows.length === 0 ? <li>{getI18n('eim-v2d-no-bmw-grouping-data', 'No BMW grouping data available.', intl)}</li> : null}
            </ul>
        </Card>

    </section>
    <Modal
        className={classNames(
            styles.countryVolumeModal,
            theme === 'light' ? styles.countryVolumeModalLight : styles.countryVolumeModalDark
        )}
        open={countryVolumeModalOpen}
        title={leaderboardModalTitle}
        onCancel={() => setCountryVolumeModalOpen(false)}
        footer={null}
        width={760}
        destroyOnHidden={true}
    >
        <div className={styles.countryVolumeSortToolbar}>
            <Button
                size="small"
                className={styles.countryVolumeSortButton}
                type={countryVolumeSortKey === 'name' ? 'primary' : 'default'}
                onClick={() => handleCountrySort('name')}
            >
                {leaderboardNameSortLabel}
                {getSortDirectionIcon(countryVolumeSortKey === 'name', countryVolumeSortDirection)}
            </Button>
            <Button
                size="small"
                className={styles.countryVolumeSortButton}
                type={countryVolumeSortKey === 'volume' ? 'primary' : 'default'}
                onClick={() => handleCountrySort('volume')}
            >
                {getI18n('eim-v2d-countries-sort-volume', 'Volume', intl)}
                {getSortDirectionIcon(countryVolumeSortKey === 'volume', countryVolumeSortDirection)}
            </Button>
        </div>

        <div className={styles.countryVolumeTableWrapper}>
            <table className={styles.countryVolumeTable}>
                <thead>
                    <tr>
                        <th>{getI18n('eim-v2d-countries-col-rank', '#', intl)}</th>
                        <th>{leaderboardNameColumnLabel}</th>
                        <th>{getI18n('eim-v2d-countries-col-volume', 'Volume', intl)}</th>
                        <th>{getI18n('eim-v2d-countries-col-total-errors', 'Total errors', intl)}</th>
                        <th>{getI18n('eim-v2d-countries-col-error-rate', 'Error rate', intl)}</th>
                        <th>{getI18n('eim-v2d-countries-col-share', 'Share', intl)}</th>
                    </tr>
                </thead>
                <tbody>
                    {sortedCountryVolumeRows.map((row) => (
                        <tr key={row.code}>
                            <td>{row.rank}</td>
                            <td>
                                {row.countryCode ? (
                                    <CountryFlagLabel countryCode={row.countryCode} label={row.name} />
                                ) : row.name}
                            </td>
                            <td>{intl.formatNumber(row.totalProcessed)}</td>
                            <td>{intl.formatNumber(row.totalErrors)}</td>
                            <td>{formatPercent(row.errorRate, intl)}</td>
                            <td>{`${intl.formatNumber(row.sharePercent, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}</td>
                        </tr>
                    ))}
                    {sortedCountryVolumeRows.length === 0 ? (
                        <tr>
                            <td colSpan={6}>{getI18n('eim-v2d-no-country-volume-data', 'No country volume data available.', intl)}</td>
                        </tr>
                    ) : null}
                </tbody>
                {sortedCountryVolumeRows.length > 0 ? (
                    <tfoot>
                        <tr className={styles.countryVolumeTotalRow}>
                            <td colSpan={2} className={styles.countryVolumeTotalLabel}>
                                {getI18n('eim-total-label', 'Total', intl)}:
                            </td>
                            <td>
                                {intl.formatNumber(countryVolumeTotals.totalVolume)}
                            </td>
                            <td>
                                {intl.formatNumber(countryVolumeTotals.totalErrors)}
                            </td>
                            <td>
                                {formatPercent(countryVolumeTotals.avgErrorRate, intl)}
                            </td>
                            <td>—</td>
                        </tr>
                    </tfoot>
                ) : null}
            </table>
        </div>
    </Modal>
    <Modal
        className={classNames(
            styles.countryVolumeModal,
            theme === 'light' ? styles.countryVolumeModalLight : styles.countryVolumeModalDark
        )}
        open={groupingModalOpen}
        title={groupingModalTitle}
        onCancel={() => setGroupingModalOpen(false)}
        footer={null}
        width={760}
        destroyOnHidden={true}
    >
        <div className={styles.countryVolumeTableWrapper}>
            <table className={styles.countryVolumeTable}>
                <thead>
                    <tr>
                        <th>{getI18n('eim-v2d-countries-col-rank', '#', intl)}</th>
                        <th>{getI18n('eim-v2d-countries-col-grouping', 'BMW Grouping', intl)}</th>
                        <th>{getI18n('eim-v2d-countries-col-volume', 'Volume', intl)}</th>
                        <th>{getI18n('eim-v2d-countries-col-total-errors', 'Total errors', intl)}</th>
                        <th>{getI18n('eim-v2d-countries-col-error-rate', 'Error rate', intl)}</th>
                        <th>{getI18n('eim-v2d-countries-col-share', 'Share', intl)}</th>
                    </tr>
                </thead>
                <tbody>
                    {groupingRowsWithShare.map((row) => (
                        <tr key={row.code}>
                            <td>{row.rank}</td>
                            <td>{row.name}</td>
                            <td>{intl.formatNumber(row.totalProcessed)}</td>
                            <td>{intl.formatNumber(row.totalErrors)}</td>
                            <td>{formatPercent(row.errorRate, intl)}</td>
                            <td>{`${intl.formatNumber(row.sharePercent, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`}</td>
                        </tr>
                    ))}
                    {groupingRowsWithShare.length === 0 ? (
                        <tr>
                            <td colSpan={6}>{getI18n('eim-v2d-no-bmw-grouping-data', 'No BMW grouping data available.', intl)}</td>
                        </tr>
                    ) : null}
                </tbody>
                {groupingRowsWithShare.length > 0 ? (
                    <tfoot>
                        <tr className={styles.countryVolumeTotalRow}>
                            <td colSpan={2} className={styles.countryVolumeTotalLabel}>
                                {getI18n('eim-total-label', 'Total', intl)}:
                            </td>
                            <td>
                                {intl.formatNumber(groupingTotals.totalVolume)}
                            </td>
                            <td>
                                {intl.formatNumber(groupingTotals.totalErrors)}
                            </td>
                            <td>
                                {formatPercent(groupingTotals.avgErrorRate, intl)}
                            </td>
                            <td>—</td>
                        </tr>
                    </tfoot>
                ) : null}
            </table>
        </div>
    </Modal>
    </>
    );
};

const WORLD_MAP_NAME = 'eim-world-v2';

const BMW_WORLD_SHAPE_NAME_SET = new Set(
    (worldEditableShapeDataset.shapeNames ?? []).map((shapeName) => shapeName.toLowerCase())
);

const WORLD_GEO_SHAPE_NAME_SET = new Set(
    (worldGeoJson.features ?? [])
        .map((feature: any) => feature?.properties?.name)
        .filter((shapeName: unknown): shapeName is string => typeof shapeName === 'string' && shapeName.trim().length > 0)
        .map((shapeName: string) => shapeName.toLowerCase())
);

const BMW_MAP_NAME_TO_WORLD_NAME_OVERRIDES: Record<string, string> = {
    USA: 'United States',
};

type BmwTerritoryMode = 'allTerritories' | 'mainlandOnly';

type BmwCountryTerritoryRule = {
    mapName: string;
    territoryMode: BmwTerritoryMode;
};

const BMW_COUNTRY_TERRITORY_RULES: Record<string, BmwCountryTerritoryRule> = {
    FR: { mapName: 'France', territoryMode: 'mainlandOnly' },
    US: { mapName: 'USA', territoryMode: 'mainlandOnly' },
    GB: { mapName: 'United Kingdom', territoryMode: 'mainlandOnly' },
    NL: { mapName: 'Netherlands', territoryMode: 'mainlandOnly' },
    DK: { mapName: 'Denmark', territoryMode: 'mainlandOnly' },
    NO: { mapName: 'Norway', territoryMode: 'mainlandOnly' },
    PT: { mapName: 'Portugal', territoryMode: 'mainlandOnly' },
    ES: { mapName: 'Spain', territoryMode: 'mainlandOnly' },
    GF: { mapName: 'Guyane (French Guiana)', territoryMode: 'allTerritories' },
};

const BMW_ISO2_TO_MAP_NAME_OVERRIDES: Record<string, string> = {
    ...Object.keys(BMW_COUNTRY_TERRITORY_RULES).reduce<Record<string, string>>((accumulator, isoCode) => {
        accumulator[isoCode] = BMW_COUNTRY_TERRITORY_RULES[isoCode].mapName;
        return accumulator;
    }, {}),
};

const BMW_MAP_NAME_TERRITORY_RULES = Object.values(BMW_COUNTRY_TERRITORY_RULES).reduce<Record<string, BmwCountryTerritoryRule>>((accumulator, rule) => {
    accumulator[rule.mapName] = rule;

    const worldMapName = BMW_MAP_NAME_TO_WORLD_NAME_OVERRIDES[rule.mapName];
    if (worldMapName) {
        accumulator[worldMapName] = rule;
    }

    return accumulator;
}, {});

const BMW_OVERSEAS_POLYGON_DISTANCE_THRESHOLD = 30;

const getBmwMapCountryName = (countryCode: string, localizedCountryName?: string | null) => {
    const overriddenName = BMW_ISO2_TO_MAP_NAME_OVERRIDES[countryCode];
    const candidateName = overriddenName ?? localizedCountryName ?? null;

    if (!candidateName) {
        return null;
    }

    const trimmedCandidateName = candidateName.trim();
    const resolvedWorldMapName = BMW_MAP_NAME_TO_WORLD_NAME_OVERRIDES[trimmedCandidateName] ?? trimmedCandidateName;
    const normalizedWorldName = resolvedWorldMapName.toLowerCase();

    if (WORLD_GEO_SHAPE_NAME_SET.has(normalizedWorldName)) {
        return resolvedWorldMapName;
    }

    const normalizedCandidateName = trimmedCandidateName.toLowerCase();
    if (BMW_WORLD_SHAPE_NAME_SET.has(normalizedCandidateName)) {
        return trimmedCandidateName;
    }

    return null;
};

const getPolygonOuterRing = (polygon: any): number[][] => {
    if (!Array.isArray(polygon) || !Array.isArray(polygon[0])) {
        return [];
    }

    return polygon[0].filter((point: any) => (
        Array.isArray(point)
        && typeof point[0] === 'number'
        && typeof point[1] === 'number'
    ));
};

const getRingArea = (ring: number[][]) => {
    if (ring.length < 3) {
        return 0;
    }

    let area = 0;
    for (let index = 0; index < ring.length; index += 1) {
        const [x1, y1] = ring[index];
        const [x2, y2] = ring[(index + 1) % ring.length];
        area += (x1 * y2) - (x2 * y1);
    }

    return Math.abs(area) / 2;
};

const getPolygonArea = (polygon: any) => getRingArea(getPolygonOuterRing(polygon));

const getPolygonCentroid = (polygon: any): [number, number] | null => {
    const ring = getPolygonOuterRing(polygon);
    if (!ring.length) {
        return null;
    }

    let sumX = 0;
    let sumY = 0;
    for (const [x, y] of ring) {
        sumX += x;
        sumY += y;
    }

    return [sumX / ring.length, sumY / ring.length];
};

const getCentroidDistance = (left: [number, number] | null, right: [number, number] | null) => {
    if (!left || !right) {
        return Number.POSITIVE_INFINITY;
    }

    const dx = left[0] - right[0];
    const dy = left[1] - right[1];
    return Math.hypot(dx, dy);
};

const getBmwMainlandPolygons = (polygons: any[]) => {
    if (polygons.length <= 1) {
        return polygons;
    }

    let primaryIndex = 0;
    let largestArea = 0;

    polygons.forEach((polygon, index) => {
        const area = getPolygonArea(polygon);
        if (area > largestArea) {
            largestArea = area;
            primaryIndex = index;
        }
    });

    const primaryCentroid = getPolygonCentroid(polygons[primaryIndex]);
    const filteredPolygons = polygons.filter((polygon, index) => {
        if (index === primaryIndex) {
            return true;
        }

        const polygonCentroid = getPolygonCentroid(polygon);
        return getCentroidDistance(primaryCentroid, polygonCentroid) <= BMW_OVERSEAS_POLYGON_DISTANCE_THRESHOLD;
    });

    return filteredPolygons.length ? filteredPolygons : [polygons[primaryIndex]];
};

const getBmwTerritoryWorldGeoJson = (sourceGeoJson: any) => {
    const clonedGeoJson = structuredClone(sourceGeoJson);
    const features = Array.isArray(clonedGeoJson?.features) ? clonedGeoJson.features : [];

    for (const feature of features) {
        if (feature?.geometry?.type !== 'MultiPolygon') {
            continue;
        }

        const featureName = feature?.properties?.name;
        const territoryRule = featureName ? BMW_MAP_NAME_TERRITORY_RULES[featureName] : null;
        if (territoryRule?.territoryMode !== 'mainlandOnly') {
            continue;
        }

        const polygons = Array.isArray(feature.geometry.coordinates) ? feature.geometry.coordinates : [];
        feature.geometry.coordinates = getBmwMainlandPolygons(polygons);
    }

    return clonedGeoJson;
};

type EimExecutiveDashboardV2Request = {
    fromDate?: string;
    toDate?: string;
    partnerno?: string;
    company?: string[];
    reference?: string;
    process?: string[];
    m_interface?: string[];
    status?: string[];
    limit?: number;
    offset?: number;
    sortField?: string;
    sortOrder?: string;
    filters?: string;
    search?: string;
    companyGroup?: string;
    partner_country?: string;
};

const toRequestDate = (value: dayjs.Dayjs): string => value.format('YYYY-MM-DD');

const toExecutiveRequestForRange = (
    start: dayjs.Dayjs,
    end: dayjs.Dayjs,
    partnerCountry?: string
): EimExecutiveDashboardRequest => {
    const normalizedPartnerCountry = (partnerCountry ?? '').trim();
    const request: EimExecutiveDashboardRequest = {
        periodStart: toRequestDate(start) as any,
        periodEnd: toRequestDate(end) as any,
    };

    if (normalizedPartnerCountry.length > 0) {
        (request as any).partnerCountry = normalizedPartnerCountry;
    }

    return request;
};

const toDashboardRequestForRange = (start: dayjs.Dayjs, end: dayjs.Dayjs): EimExecutiveDashboardV2Request => ({
    fromDate: toRequestDate(start),
    toDate: toRequestDate(end),
});

const getCurrentPeriodRange = (
    periodOption: PeriodOption,
    customPeriodRange: [Dayjs, Dayjs] | null,
    reference: dayjs.Dayjs = dayjs()
) => {
    if (periodOption === 'custom' && customPeriodRange?.[0] && customPeriodRange?.[1]) {
        return {
            start: customPeriodRange[0].startOf('day'),
            end: customPeriodRange[1].endOf('day'),
        };
    }

    if (periodOption === 'currentWeek') {
        return {
            start: reference.subtract(6, 'day').startOf('day'),
            end: reference.endOf('day'),
        };
    }

    if (periodOption === 'currentMonth') {
        return {
            start: reference.startOf('month'),
            end: reference.endOf('day'),
        };
    }

    if (periodOption === 'currentQuarter') {
        return {
            start: reference.startOf('quarter'),
            end: reference.endOf('day'),
        };
    }

    if (periodOption === 'currentYear') {
        return {
            start: reference.startOf('year'),
            end: reference.endOf('day'),
        };
    }

    return {
        start: reference.startOf('day'),
        end: reference.endOf('day'),
    };
};

const getPreviousPeriodRange = (periodOption: PeriodOption, currentStart: dayjs.Dayjs, currentEnd: dayjs.Dayjs) => {
    if (periodOption === 'currentMonth') {
        return {
            start: currentStart.subtract(1, 'month').startOf('month'),
            end: currentStart.subtract(1, 'month').endOf('month'),
        };
    }

    if (periodOption === 'currentQuarter') {
        return {
            start: currentStart.subtract(1, 'quarter').startOf('quarter'),
            end: currentStart.subtract(1, 'quarter').endOf('quarter'),
        };
    }

    if (periodOption === 'currentYear') {
        return {
            start: currentStart.subtract(1, 'year').startOf('year'),
            end: currentStart.subtract(1, 'year').endOf('year'),
        };
    }

    const durationMs = Math.max(0, currentEnd.diff(currentStart, 'millisecond'));
    const previousEnd = currentStart.subtract(1, 'millisecond');
    const previousStart = previousEnd.subtract(durationMs, 'millisecond');

    return {
        start: previousStart,
        end: previousEnd,
    };
};

const formatPeriodLabel = (periodOption: PeriodOption, range: { start: Dayjs; end: Dayjs }, intl: IAppComponentProps['intl'], isComparison = false): string => {
    if (periodOption === 'today') {
        return isComparison
            ? getI18n('eim-v2d-period-yesterday', 'Yesterday', intl)
            : getI18n('eim-period-today', 'Today', intl);
    }
    if (periodOption === 'currentWeek') {
        return isComparison
            ? getI18n('eim-v2d-period-previous-week', 'Previous week', intl)
            : getI18n('eim-period-currentWeek', 'Current week', intl);
    }
    if (periodOption === 'currentMonth') {
        return isComparison
            ? getI18n('eim-v2d-period-previous-month', 'Previous month', intl)
            : getI18n('eim-period-currentMonth', 'Current month', intl);
    }
    if (periodOption === 'currentQuarter') {
        return isComparison
            ? getI18n('eim-v2d-period-previous-quarter', 'Previous quarter', intl)
            : getI18n('eim-period-currentQuarter', 'Current quarter', intl);
    }
    if (periodOption === 'currentYear') {
        return isComparison
            ? getI18n('eim-v2d-period-previous-year', 'Previous year', intl)
            : getI18n('eim-period-currentYear', 'Current year', intl);
    }
    return `${range.start.format('MMM D, YYYY')} - ${range.end.format('MMM D, YYYY')}`;
};

const buildPeriodCacheToken = (periodOption: PeriodOption, customPeriodRange: [Dayjs, Dayjs] | null): string => {
    const reference = dayjs();

    if (periodOption === 'custom') {
        const customStart = customPeriodRange?.[0];
        const customEnd = customPeriodRange?.[1];
        if (!customStart || !customEnd) {
            return 'custom:incomplete';
        }

        return `custom:${customStart.toISOString()}:${customEnd.toISOString()}`;
    }

    if (periodOption === 'today') {
        return `today:${reference.format('YYYY-MM-DD')}`;
    }

    if (periodOption === 'currentWeek') {
        return `currentWeek:${reference.subtract(6, 'day').format('YYYY-MM-DD')}:${reference.format('YYYY-MM-DD')}`;
    }

    if (periodOption === 'currentMonth') {
        return `currentMonth:${reference.startOf('month').format('YYYY-MM-DD')}`;
    }

    if (periodOption === 'currentQuarter') {
        return `currentQuarter:${reference.startOf('quarter').format('YYYY-MM-DD')}`;
    }

    if (periodOption === 'currentYear') {
        return `currentYear:${reference.startOf('year').format('YYYY-MM-DD')}`;
    }

    return `${periodOption}:${reference.format('YYYY-MM-DD')}`;
};

const toPartnerRequestForRange = (
    company: string[],
    fromDate: dayjs.Dayjs,
    toDate: dayjs.Dayjs,
    partnerCountry?: string
): EimExecutiveDashboardV2Request => ({
    fromDate: toRequestDate(fromDate),
    toDate: toRequestDate(toDate),
    company,
    partner_country: partnerCountry,
});

const toPartnerRequest = (company: string[], partnerCountry?: string, periodRange?: { start: dayjs.Dayjs; end: dayjs.Dayjs }): EimExecutiveDashboardV2Request => ({
    ...toPartnerRequestForRange(
        company,
        periodRange?.start ?? dayjs().startOf('day'),
        periodRange?.end ?? dayjs().endOf('day'),
        partnerCountry
    ),
});

const getMapName = (countryCode: string, regionNames: Intl.DisplayNames | null) => {
    if (!regionNames) {
        return getBmwMapCountryName(countryCode, null);
    }

    try {
        const resolved = regionNames.of(countryCode);
        return getBmwMapCountryName(countryCode, resolved ?? null);
    } catch {
        return null;
    }
};

const escapePopupHtml = (value: string): string => value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');

type ExecutiveDashboardV2CacheSnapshot = {
    countryMetrics: CountryMetric[];
    todayCountryMetrics: CountryMetric[];
    yesterdayCountryMetrics: CountryMetric[];
    systemMetrics?: SystemMetric[];
    globalInvoiceTrendRows: HourlyTrendRow[];
    globalTopErrorMessages?: CountryErrorMessageRow[];
    groupMetrics?: GroupMetric[];
    selectedCountryCode?: string;
    countryDetailsByCode?: Record<string, {
        partnerSummaryRows: PartnerSummaryRow[];
        hourlyTrendRows: HourlyTrendRow[];
        countryDetailMetrics: CountryDetailMetrics | null;
        countryDetailTopErrorMessages: CountryErrorMessageRow[];
    }>;
};

type ExecutiveDashboardV2CacheEntry = {
    updatedAt: number;
    snapshot: ExecutiveDashboardV2CacheSnapshot;
};

const EXECUTIVE_DASHBOARD_V2_CACHE_PREFIX = 'eimExecutiveDashboardV2Cache:v2';
const EXECUTIVE_DASHBOARD_V2_CACHE_TTL_MS = 10 * 60 * 1000;
const ALL_COUNTRIES_CACHE_KEY = '__ALL_COUNTRIES__';
const ERROR_FEED_CRITICAL_PERCENT_THRESHOLD = 20;

const EIMExecutiveDashboardV2: FunctionComponent<IAppComponentProps> = (props) => { // NOSONAR - Container coordinates many memoized dashboard view models; behavior-preserving refactor already extracted major computation blocks.
    const { intl } = props;
    const navigate = useNavigate();

    const [isLoading, setIsLoading] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [countryMetrics, setCountryMetrics] = useState<CountryMetric[]>([]);
    const dispatch = useAppDispatch();
    const dashboardFilters = useAppSelector((state) => state.eimDashboardFilters);
    const selectedCountryCode = dashboardFilters.countryCode;
    const [partnerSummaryRows, setPartnerSummaryRows] = useState<PartnerSummaryRow[]>([]);
    const [hourlyTrendRows, setHourlyTrendRows] = useState<HourlyTrendRow[]>([]);
    const [countryDetailMetrics, setCountryDetailMetrics] = useState<CountryDetailMetrics | null>(null);
    const [countryDetailTopErrorMessages, setCountryDetailTopErrorMessages] = useState<CountryErrorMessageRow[]>([]);
    const [globalTopErrorMessages, setGlobalTopErrorMessages] = useState<CountryErrorMessageRow[]>([]);
    const [isCountryDetailLoading, setIsCountryDetailLoading] = useState(false);
    const [todayCountryMetrics, setTodayCountryMetrics] = useState<CountryMetric[]>([]);
    const [yesterdayCountryMetrics, setYesterdayCountryMetrics] = useState<CountryMetric[]>([]);
    const [systemMetrics, setSystemMetrics] = useState<SystemMetric[]>([]);
    const [globalInvoiceTrendRows, setGlobalInvoiceTrendRows] = useState<HourlyTrendRow[]>([]);
    const [hoveredDonutSegment, setHoveredDonutSegment] = useState<number | null>(null);
    const [partnersModalOpen, setPartnersModalOpen] = useState(false);
    const [documentationModalOpen, setDocumentationModalOpen] = useState(false);
    const [groupMetrics, setGroupMetrics] = useState<GroupMetric[]>([]);
    const [theme, setTheme] = useState<ThemeMode>('dark');
    const selectedPeriodOption = dashboardFilters.periodOption as PeriodOption;
    const customPeriodRange = dashboardFilters.customPeriodStart && dashboardFilters.customPeriodEnd
        ? [dayjs(dashboardFilters.customPeriodStart), dayjs(dashboardFilters.customPeriodEnd)] as [Dayjs, Dayjs]
        : null;

    const [getCountryMetrics, { loading: isCountryMetricsLoading }] = useLazyQuery(QUERY_EIM_EXECUTIVE_DASHBOARD_V2_COUNTRY_METRICS);
    const [getYesterdayCountryMetrics, { loading: isYesterdayCountryMetricsLoading }] = useLazyQuery(QUERY_EIM_EXECUTIVE_DASHBOARD_V2_COUNTRY_METRICS);
    const [getSystemMetrics, { loading: isSystemMetricsLoading }] = useLazyQuery(QUERY_EIM_EXECUTIVE_DASHBOARD_SYSTEM_METRICS);
    const [getGroupMetrics, { loading: isGroupMetricsLoading }] = useLazyQuery(QUERY_EIM_EXECUTIVE_DASHBOARD_GROUP_METRICS);
    const [getActivePartnerCountryDetail, { loading: isActivePartnerCountryDetailLoading }] = useLazyQuery(QUERY_EIM_EXECUTIVE_DASHBOARD_V2_COUNTRY_DETAIL);

    const refreshStartedAtRef = useRef<number | null>(null);
    const skipCacheForManualRefreshRef = useRef(false);

    const isNetworkLoading = isLoading
        || isCountryDetailLoading
        || isCountryMetricsLoading
        || isYesterdayCountryMetricsLoading
        || isSystemMetricsLoading
        || isGroupMetricsLoading
        || isActivePartnerCountryDetailLoading;

    const isAnyDashboardRequestLoading = isNetworkLoading || isRefreshing;

    const companyCountryLookup = useMemo(() => buildCompanyCountryLookup(countryMetrics), [countryMetrics]);

    // Log hourlyTrendRows changes
    useEffect(() => {
        console.log('[hourlyTrendRows state changed] New length:', hourlyTrendRows.length, 'First 3 rows:', hourlyTrendRows.slice(0, 3));
    }, [hourlyTrendRows]);

    useEffect(() => {
        try {
            echarts.registerMap(WORLD_MAP_NAME, getBmwTerritoryWorldGeoJson(worldGeoJson));
        } catch {
            // Map may already be registered after hot reload.
        }
    }, []);

    useEffect(() => {
        try {
            const storedTheme = globalThis.localStorage.getItem(EIM_THEME_STORAGE_KEY);
            if (storedTheme === 'light' || storedTheme === 'dark') {
                setTheme(storedTheme);
            }
        } catch {
            // Ignore localStorage access failures.
        }
    }, []);

    useEffect(() => {
        try {
            globalThis.localStorage.setItem(EIM_THEME_STORAGE_KEY, theme);
        } catch {
            // Ignore localStorage access failures.
        }
    }, [theme]);

    const isLightTheme = theme === 'light';

    const selectedPeriodRange = useMemo(
        () => getCurrentPeriodRange(selectedPeriodOption, customPeriodRange),
        [customPeriodRange, selectedPeriodOption]
    );

    const comparisonPeriodRange = useMemo(
        () => getPreviousPeriodRange(selectedPeriodOption, selectedPeriodRange.start, selectedPeriodRange.end),
        [selectedPeriodOption, selectedPeriodRange.end, selectedPeriodRange.start]
    );

    const isCustomDateRangeIncomplete = useMemo(
        () => selectedPeriodOption === 'custom' && !(customPeriodRange?.[0] && customPeriodRange?.[1]),
        [customPeriodRange, selectedPeriodOption]
    );

    const periodDisplayLabel = useMemo(() => {
        return formatPeriodLabel(selectedPeriodOption, selectedPeriodRange, intl);
    }, [intl, selectedPeriodOption, selectedPeriodRange]);

    const comparisonPeriodDisplayLabel = useMemo(() => {
        return formatPeriodLabel(selectedPeriodOption, comparisonPeriodRange, intl, true);
    }, [comparisonPeriodRange, intl, selectedPeriodOption]);

    const trendComparisonSuffix = useMemo(() => {
        return `${getI18n('eim-v2d-vs-label', 'vs.', intl)} ${comparisonPeriodDisplayLabel}`;
    }, [comparisonPeriodDisplayLabel, intl]);

    const handleToggleTheme = useCallback(() => {
        setTheme((currentTheme) => (currentTheme === 'dark' ? 'light' : 'dark'));
    }, []);

    const navigateToOperationalDataWithFilters = useCallback((options: {
        type?: 'IN' | 'OUT';
        statusFilters?: string[];
    }) => {
        const type = options.type ?? null;
        const statusFilters = options.statusFilters ?? [];
        dispatch(setTileFilters({ type, statusFilters }));
        globalThis.sessionStorage.setItem(OPERATIONAL_DATA_ORIGIN_KEY, EXECUTIVE_DASHBOARD_V2_ROUTE);
        globalThis.sessionStorage.setItem('fromDate', selectedPeriodRange.start.format('YYYY-MM-DD'));
        globalThis.sessionStorage.setItem('toDate', selectedPeriodRange.end.format('YYYY-MM-DD'));
        globalThis.sessionStorage.setItem('eimMonitoringPeriodLabel', periodDisplayLabel);
        globalThis.sessionStorage.removeItem('company');
        globalThis.sessionStorage.removeItem('process');
        globalThis.sessionStorage.removeItem('companyGroup');
        if (selectedCountryCode) {
            globalThis.sessionStorage.setItem('partnerCountry', selectedCountryCode);
        } else {
            globalThis.sessionStorage.removeItem('partnerCountry');
        }
        globalThis.sessionStorage.removeItem('systemIds');

        if (type) {
            globalThis.sessionStorage.setItem('type', type);
        } else {
            globalThis.sessionStorage.removeItem('type');
        }

        if (statusFilters.length > 0) {
            globalThis.sessionStorage.setItem('statusFilters', JSON.stringify(statusFilters));
        } else {
            globalThis.sessionStorage.removeItem('statusFilters');
        }

        navigate('/eim-document-overview-monitoring');
    }, [dispatch, navigate, periodDisplayLabel, selectedCountryCode, selectedPeriodRange]);

    const getDashboardCacheKey = useCallback(() => {
        const locale = globalThis.localStorage.getItem('locale') || 'en';
        const periodCacheToken = buildPeriodCacheToken(selectedPeriodOption, customPeriodRange);
        return `${EXECUTIVE_DASHBOARD_V2_CACHE_PREFIX}:${locale}:${periodCacheToken}`;
    }, [customPeriodRange, selectedPeriodOption]);

    const readFreshDashboardCacheEntry = useCallback((cacheKey: string): ExecutiveDashboardV2CacheEntry | null => {
        const cachedView = globalThis.sessionStorage.getItem(cacheKey);
        if (!cachedView) {
            return null;
        }

        try {
            const parsedEntry = JSON.parse(cachedView) as ExecutiveDashboardV2CacheEntry;
            const isFreshEntry = Number.isFinite(parsedEntry?.updatedAt)
                && (Date.now() - parsedEntry.updatedAt) <= EXECUTIVE_DASHBOARD_V2_CACHE_TTL_MS
                && parsedEntry?.snapshot !== undefined;

            if (!isFreshEntry) {
                globalThis.sessionStorage.removeItem(cacheKey);
                return null;
            }

            return parsedEntry;
        } catch {
            globalThis.sessionStorage.removeItem(cacheKey);
            return null;
        }
    }, []);

    const hydrateDetailStateFromSnapshot = useCallback((_snapshot: ExecutiveDashboardV2CacheSnapshot) => {
        // Always reset detail state to empty — the country detail useEffect will fetch fresh data.
        // Never load hourlyTrendRows or partnerSummaryRows from cache; they may be stale/company-filtered.
        setPartnerSummaryRows([]);
        setHourlyTrendRows([]);
        setCountryDetailMetrics(null);
        setCountryDetailTopErrorMessages([]);
    }, []);

    const hydrateDashboardFromCache = useCallback((snapshot: ExecutiveDashboardV2CacheSnapshot) => {
        setCountryMetrics(snapshot.countryMetrics ?? []);
        setTodayCountryMetrics(snapshot.todayCountryMetrics ?? []);
        setYesterdayCountryMetrics(snapshot.yesterdayCountryMetrics ?? []);
        setSystemMetrics(snapshot.systemMetrics ?? []);
        setGlobalInvoiceTrendRows(snapshot.globalInvoiceTrendRows ?? []);
        setGlobalTopErrorMessages(snapshot.globalTopErrorMessages ?? []);
        setGroupMetrics(snapshot.groupMetrics ?? []);
        hydrateDetailStateFromSnapshot(snapshot);
    }, [hydrateDetailStateFromSnapshot]);

    const tryHydrateDashboardFromCache = useCallback((cacheKey: string): boolean => {
        if (skipCacheForManualRefreshRef.current) {
            return false;
        }

        const parsedEntry = readFreshDashboardCacheEntry(cacheKey);
        if (!parsedEntry) {
            return false;
        }

        hydrateDashboardFromCache(parsedEntry.snapshot);
        return true;
    }, [hydrateDashboardFromCache, readFreshDashboardCacheEntry]);

    const writeDashboardSnapshotToCache = useCallback((
        cacheKey: string,
        snapshot: ExecutiveDashboardV2CacheSnapshot,
        preserveExistingSelections = false
    ) => {
        const existingEntry = preserveExistingSelections ? readFreshDashboardCacheEntry(cacheKey) : null;
        const hasIncomingCountryDetails = Object.keys(snapshot.countryDetailsByCode ?? {}).length > 0;
        const hasIncomingSelectedCountry = typeof snapshot.selectedCountryCode === 'string'
            && snapshot.selectedCountryCode.trim().length > 0;
        let resolvedSelectedCountryCode = snapshot.selectedCountryCode ?? '';
        if (preserveExistingSelections && !hasIncomingSelectedCountry) {
            resolvedSelectedCountryCode = existingEntry?.snapshot.selectedCountryCode ?? '';
        }

        let resolvedCountryDetailsByCode = snapshot.countryDetailsByCode ?? {};
        // Never carry over old countryDetailsByCode from previous cache — it may contain stale company-filtered data
        if (preserveExistingSelections && !hasIncomingCountryDetails) {
            resolvedCountryDetailsByCode = {};
        }

        const nextEntry: ExecutiveDashboardV2CacheEntry = {
            updatedAt: Date.now(),
            snapshot: {
                ...snapshot,
                selectedCountryCode: resolvedSelectedCountryCode,
                countryDetailsByCode: resolvedCountryDetailsByCode,
            },
        };

        try {
            globalThis.sessionStorage.setItem(cacheKey, JSON.stringify(nextEntry));
        } catch {
            globalThis.sessionStorage.removeItem(cacheKey);
        }
    }, [readFreshDashboardCacheEntry]);

    const writeCountryDetailToDashboardCache = useCallback((
        cacheKey: string,
        countryCode: string,
        payload: {
            partnerSummaryRows: PartnerSummaryRow[];
            hourlyTrendRows: HourlyTrendRow[];
            countryDetailMetrics: CountryDetailMetrics | null;
            countryDetailTopErrorMessages: CountryErrorMessageRow[];
        }
    ) => {
        const cacheEntry = readFreshDashboardCacheEntry(cacheKey);
        if (!cacheEntry) {
            return;
        }

        const normalizedCountryCode = countryCode.trim().toUpperCase();
        if (normalizedCountryCode.length === 0) {
            return;
        }

        const currentCountryDetails = cacheEntry.snapshot.countryDetailsByCode ?? {};
        const nextEntry: ExecutiveDashboardV2CacheEntry = {
            ...cacheEntry,
            snapshot: {
                ...cacheEntry.snapshot,
                selectedCountryCode: '', // Never persist country selection in cache
                countryDetailsByCode: {
                    ...currentCountryDetails,
                    [normalizedCountryCode]: payload,
                },
            },
        };

        writeDashboardSnapshotToCache(cacheKey, nextEntry.snapshot);
    }, [readFreshDashboardCacheEntry, writeDashboardSnapshotToCache]);

    const loadDashboard = useCallback(async (forceRefresh = false) => {
        if (isCustomDateRangeIncomplete) {
            setIsLoading(false);
            return;
        }

        if (forceRefresh) {
            setCountryDetailMetrics(null);
            setCountryDetailTopErrorMessages([]);
            setPartnerSummaryRows([]);
            setHourlyTrendRows([]);
            setIsCountryDetailLoading(false);
        }

        const cacheKey = getDashboardCacheKey();
        let hasWarmSnapshot = false;

        if (forceRefresh) {
            skipCacheForManualRefreshRef.current = true;
            globalThis.sessionStorage.removeItem(cacheKey);
        }

        if (!forceRefresh) {
            hasWarmSnapshot = tryHydrateDashboardFromCache(cacheKey);
            if (hasWarmSnapshot) {
                setIsLoading(false);
                return;
            }
        }

        if (!hasWarmSnapshot) {
            setIsLoading(true);
        }

        try {
            const currentRange = getCurrentPeriodRange(selectedPeriodOption, customPeriodRange);
            const previousRange = getPreviousPeriodRange(selectedPeriodOption, currentRange.start, currentRange.end);
            const executiveRequest = toExecutiveRequestForRange(currentRange.start, currentRange.end);
            const currentRequest = toDashboardRequestForRange(currentRange.start, currentRange.end);
            const yesterdayRequest = toDashboardRequestForRange(previousRange.start, previousRange.end);
            const allCountriesDetailRequest = toPartnerRequestForRange([], currentRange.start, currentRange.end);
            const [currentCountryResponse, yesterdayResponse, systemMetricsResponse, groupMetricsResponse, allCountriesDetailResponse] = await Promise.all([
                getCountryMetrics({
                    variables: { request: currentRequest },
                    fetchPolicy: 'network-only',
                }),
                getYesterdayCountryMetrics({
                    variables: { request: yesterdayRequest },
                    fetchPolicy: 'network-only',
                }),
                getSystemMetrics({
                    variables: { request: executiveRequest },
                    fetchPolicy: 'network-only',
                }),
                getGroupMetrics({
                    variables: { request: executiveRequest },
                    fetchPolicy: 'network-only',
                }),
                getActivePartnerCountryDetail({
                    variables: { request: allCountriesDetailRequest },
                    fetchPolicy: 'network-only',
                }),
            ]);

            const currentCountryPayload: CountryMetric[] = currentCountryResponse?.data?.eimExecutiveDashboardV2CountryMetrics ?? [];
            const yesterdayCountryPayload: CountryMetric[] = yesterdayResponse?.data?.eimExecutiveDashboardV2CountryMetrics ?? [];
            const safeSystemMetricsPayload: SystemMetric[] = systemMetricsResponse?.data?.eimExecutiveDashboardSystemMetrics ?? [];
            const safeGroupMetricsPayload: GroupMetric[] = groupMetricsResponse?.data?.eimExecutiveDashboardGroupMetrics ?? [];
            const globalTopErrorMessagesPayload: CountryErrorMessageRow[] = (
                allCountriesDetailResponse?.data?.eimExecutiveDashboardV2CountryDetail?.topErrorMessages ?? []
            ).slice(0, 5);

            console.log('[loadDashboard] Country metrics response - Count:', currentCountryPayload.length);
            const metricsTotal = currentCountryPayload.reduce((sum, m) => sum + (m.intake?.totalProcessed ?? 0), 0);
            const metricsFailed = currentCountryPayload.reduce((sum, m) => sum + (m.exceptions?.failedCount ?? 0), 0);
            console.log('[loadDashboard] Metrics aggregate - Total:', metricsTotal, 'Failed:', metricsFailed);

            setCountryMetrics(currentCountryPayload);
            setTodayCountryMetrics(currentCountryPayload);
            setYesterdayCountryMetrics(yesterdayCountryPayload);
            setGlobalTopErrorMessages(globalTopErrorMessagesPayload);
            setSystemMetrics(safeSystemMetricsPayload);
            setGroupMetrics(safeGroupMetricsPayload);

            writeDashboardSnapshotToCache(cacheKey, {
                countryMetrics: currentCountryPayload,
                todayCountryMetrics: currentCountryPayload,
                yesterdayCountryMetrics: yesterdayCountryPayload,
                systemMetrics: safeSystemMetricsPayload,
                globalInvoiceTrendRows: [],
                globalTopErrorMessages: globalTopErrorMessagesPayload,
                groupMetrics: safeGroupMetricsPayload,
                selectedCountryCode: '',
                countryDetailsByCode: {},
            }, true);

            setGlobalInvoiceTrendRows([]);

            console.log('[loadDashboard] Writing to cache - cacheKey:', cacheKey, 'hourlyTrendRows to cache:', 0);
            writeDashboardSnapshotToCache(cacheKey, {
                countryMetrics: currentCountryPayload,
                todayCountryMetrics: currentCountryPayload,
                yesterdayCountryMetrics: yesterdayCountryPayload,
                systemMetrics: safeSystemMetricsPayload,
                globalInvoiceTrendRows: [],
                globalTopErrorMessages: globalTopErrorMessagesPayload,
                groupMetrics: safeGroupMetricsPayload,
                selectedCountryCode: '',
                countryDetailsByCode: {},
            }, true);

        } finally {
            setIsLoading(false);
        }
    }, [customPeriodRange, getActivePartnerCountryDetail, getCountryMetrics, getDashboardCacheKey, getGroupMetrics, getSystemMetrics, getYesterdayCountryMetrics, isCustomDateRangeIncomplete, selectedPeriodOption, tryHydrateDashboardFromCache, writeDashboardSnapshotToCache]);

    const loadReactiveGroupMetrics = useCallback(async () => {
        if (isCustomDateRangeIncomplete) {
            setGroupMetrics([]);
            return;
        }

        const currentRange = getCurrentPeriodRange(selectedPeriodOption, customPeriodRange);
        const request = toExecutiveRequestForRange(currentRange.start, currentRange.end);

        const response = await getGroupMetrics({
            variables: { request },
            fetchPolicy: 'network-only',
        });

        const rows: GroupMetric[] = response?.data?.eimExecutiveDashboardGroupMetrics ?? [];
        setGroupMetrics(rows);
    }, [customPeriodRange, getGroupMetrics, isCustomDateRangeIncomplete, selectedCountryCode, selectedPeriodOption]);

    useEffect(() => {
        loadReactiveGroupMetrics().catch(() => {
            setGroupMetrics([]);
        });
    }, [loadReactiveGroupMetrics]);

    useEffect(() => {
        loadDashboard().catch(() => {
            setIsLoading(false);
            setCountryMetrics([]);
            setTodayCountryMetrics([]);
            setYesterdayCountryMetrics([]);
            setSystemMetrics([]);
            setGlobalInvoiceTrendRows([]);
            setGlobalTopErrorMessages([]);
            setGroupMetrics([]);
        });
    }, [loadDashboard]);

    useEffect(() => {
        if (!isRefreshing) {
            return undefined;
        }

        if (isNetworkLoading) {
            return undefined;
        }

        const startedAt = refreshStartedAtRef.current ?? Date.now();
        const minVisibleMs = 500;
        const elapsedMs = Date.now() - startedAt;
        const remainingMs = Math.max(0, minVisibleMs - elapsedMs);

        const timeoutHandle = globalThis.setTimeout(() => {
            setIsRefreshing(false);
            refreshStartedAtRef.current = null;
            skipCacheForManualRefreshRef.current = false;
        }, remainingMs);

        return () => {
            globalThis.clearTimeout(timeoutHandle);
        };
    }, [isNetworkLoading, isRefreshing]);

    const regionNames = useMemo(() => {
        if (!Intl.DisplayNames) {
            return null;
        }

        return new Intl.DisplayNames(['en'], { type: 'region' });
    }, []);

    const worldMapPoints = useMemo<WorldMapPoint[]>(() => {
        const countryAccumulator = new Map<string, {
            countryCode: string;
            name: string;
            totalProcessed: number;
            failedCount: number;
            overdueCount: number;
            inTransitCount: number;
            weightedStpRateNumerator: number;
            weightedTransitDaysNumerator: number;
        }>();

        countryMetrics.forEach((countryMetric) => {
            const countryCode = resolveCountryCode(countryMetric, companyCountryLookup);
            if (countryCode.length !== 2) {
                return;
            }

            const mapName = getMapName(countryCode, regionNames);
            if (!mapName) {
                return;
            }

            const totalProcessed = Number(countryMetric.intake?.totalProcessed ?? 0);
            const stpRate = Number(countryMetric.processing?.stpRate ?? 0);
            const avgTransitDays = Number(countryMetric.processing?.avgTransitDays ?? 0);
            const failedCount = Number(countryMetric.exceptions?.failedCount ?? 0);
            const overdueCount = Number(countryMetric.exceptions?.overdueCount ?? 0);
            const inTransitCount = Number(countryMetric.processing?.inTransitCount ?? 0);

            const current = countryAccumulator.get(countryCode);
            if (!current) {
                countryAccumulator.set(countryCode, {
                    countryCode,
                    name: mapName,
                    totalProcessed,
                    failedCount,
                    overdueCount,
                    inTransitCount,
                    weightedStpRateNumerator: stpRate * totalProcessed,
                    weightedTransitDaysNumerator: avgTransitDays * totalProcessed,
                });
                return;
            }

            current.totalProcessed += totalProcessed;
            current.failedCount += failedCount;
            current.overdueCount += overdueCount;
            current.inTransitCount += inTransitCount;
            current.weightedStpRateNumerator += stpRate * totalProcessed;
            current.weightedTransitDaysNumerator += avgTransitDays * totalProcessed;
        });

        return Array.from(countryAccumulator.values()).map((country) => {
            const safeTotal = Math.max(1, country.totalProcessed);
            return {
                countryCode: country.countryCode,
                name: country.name,
                value: country.totalProcessed,
                totalProcessed: country.totalProcessed,
                stpRate: country.totalProcessed > 0 ? country.weightedStpRateNumerator / safeTotal : 0,
                avgTransitDays: country.totalProcessed > 0 ? country.weightedTransitDaysNumerator / safeTotal : 0,
                failedCount: country.failedCount,
                overdueCount: country.overdueCount,
                inTransitCount: country.inTransitCount,
            };
        });
    }, [companyCountryLookup, countryMetrics, regionNames]);

    const rawCountryVolume = useMemo(() => getCountryMetricTotalProcessed(countryMetrics), [countryMetrics]);

    const mappedCountryVolume = useMemo(() => worldMapPoints.reduce(
        (sum, point) => sum + Number(point.totalProcessed ?? 0),
        0
    ), [worldMapPoints]);

    const unmappedCountryVolume = useMemo(
        () => Math.max(0, rawCountryVolume - mappedCountryVolume),
        [mappedCountryVolume, rawCountryVolume]
    );

    const unmappedFailureRows = useMemo<UnmappedFailureRow[]>(() => {
        const byCountryAndCompany = new Map<string, UnmappedFailureRow>();

        countryMetrics.forEach((countryMetric) => {
            const countryCode = resolveCountryCode(countryMetric, companyCountryLookup);
            const hasMappableCountryCode = countryCode.length === 2 && Boolean(getMapName(countryCode, regionNames));
            if (hasMappableCountryCode) {
                return;
            }

            const failedCount = Math.max(0, Number(countryMetric.exceptions?.failedCount ?? 0));
            const overdueCount = Math.max(0, Number(countryMetric.exceptions?.overdueCount ?? 0));
            const failures = failedCount + overdueCount;
            if (failures <= 0) {
                return;
            }

            const rawCountry = String(countryMetric.country ?? '').trim();
            const countryLabel = rawCountry.length > 0
                ? rawCountry
                : getI18n('eim-v2d-unknown-country', 'Unknown', intl);
            const companyCode = String(countryMetric.companyCode ?? '').trim() || 'UNKNOWN';
            const key = `${countryLabel.toUpperCase()}|${companyCode.toUpperCase()}`;
            const existing = byCountryAndCompany.get(key);

            if (!existing) {
                byCountryAndCompany.set(key, {
                    key,
                    countryLabel,
                    companyCode,
                    failures,
                });
                return;
            }

            existing.failures += failures;
        });

        return Array.from(byCountryAndCompany.values())
            .sort((left, right) => right.failures - left.failures)
            .slice(0, 10);
    }, [companyCountryLookup, countryMetrics, intl, regionNames]);

    const unmappedFailureTotal = useMemo(
        () => unmappedFailureRows.reduce((sum, row) => sum + row.failures, 0),
        [unmappedFailureRows]
    );

    const unmappedFailureTooltip = useMemo(() => {
        if (unmappedFailureRows.length === 0) {
            return getI18n('eim-v2d-unmapped-failures-empty', 'No unmapped failures.', intl);
        }

        const lines = unmappedFailureRows.map((row) => `${row.countryLabel} (${row.companyCode}): ${formatCount(row.failures, intl)}`);
        return [
            getI18n('eim-v2d-unmapped-failures-top-list', 'Top unmapped failure sources:', intl),
            ...lines,
        ].join('\n');
    }, [intl, unmappedFailureRows]);

    const selectedCountryMetrics = useMemo(() => {
        if (selectedCountryCode.trim().length === 0) {
            return [];
        }

        return countryMetrics
            .filter((countryMetric) => resolveCountryCode(countryMetric, companyCountryLookup) === selectedCountryCode);
    }, [companyCountryLookup, countryMetrics, selectedCountryCode]);

    const selectedCountryTodayMetrics = useMemo(() => todayCountryMetrics
        .filter((countryMetric) => resolveCountryCode(countryMetric, companyCountryLookup) === selectedCountryCode), [companyCountryLookup, selectedCountryCode, todayCountryMetrics]);

    const selectedCountryYesterdayMetrics = useMemo(() => yesterdayCountryMetrics
        .filter((countryMetric) => resolveCountryCode(countryMetric, companyCountryLookup) === selectedCountryCode), [companyCountryLookup, selectedCountryCode, yesterdayCountryMetrics]);

    const selectedCountryCompanyCodes = useMemo(() => Array.from(new Set(selectedCountryMetrics
        .map((countryMetric) => (countryMetric.companyCode ?? '').trim())
        .filter((companyCode) => companyCode.length > 0))), [selectedCountryMetrics]);

    const selectedCountrySummary = useMemo(
        () => buildSelectedCountrySummary(
            selectedCountryCode.trim().length > 0 ? selectedCountryMetrics : countryMetrics,
            countryDetailMetrics,
            selectedCountryCode.trim().length > 0
        ),
        [countryDetailMetrics, countryMetrics, selectedCountryCode, selectedCountryMetrics]
    );

    const selectedCountryHealthCategory = useMemo<WorldMapHealthCategory>(() => deriveCountryHealthCategory(selectedCountrySummary), [selectedCountrySummary]);

    const selectedCountryHealthLabel = useMemo(() => getI18n(
        HEALTH_CATEGORY_I18N_KEY[selectedCountryHealthCategory],
        getHealthDefaultLabel(selectedCountryHealthCategory),
        intl
    ), [intl, selectedCountryHealthCategory]);

    const todayMetricsAggregate = useMemo(
        () => buildCountryMetricsAggregate(todayCountryMetrics),
        [todayCountryMetrics]
    );

    useEffect(() => {
        if (selectedCountryCode.trim().length === 0) {
            if (isCustomDateRangeIncomplete) {
                setIsCountryDetailLoading(false);
                return;
            }

            // All-countries view: always fetch fresh with no company filter
            setIsCountryDetailLoading(true);
            const allCountriesRequest = toPartnerRequestForRange([], selectedPeriodRange.start, selectedPeriodRange.end);
            console.log('[useEffect - all countries] Fetching with no company filter:', allCountriesRequest);

            getActivePartnerCountryDetail({
                variables: { request: allCountriesRequest },
                fetchPolicy: 'network-only',
            }).then((response) => {
                const detail = response?.data?.eimExecutiveDashboardV2CountryDetail;
                const partnerRows = detail?.topPartners ?? [];
                const trendRows = detail?.hourlyTrend ?? [];
                const detailMetrics = detail?.metrics ?? null;
                const topErrorMessages = (detail?.topErrorMessages ?? []).slice(0, 4);

                console.log('[useEffect - all countries] Fetched - partners:', partnerRows.length, 'trend rows:', trendRows.length);
                setPartnerSummaryRows(partnerRows);
                setHourlyTrendRows(trendRows);
                setGlobalInvoiceTrendRows(trendRows);
                setCountryDetailMetrics(detailMetrics);
                setCountryDetailTopErrorMessages(topErrorMessages);
            }).catch(() => {
                setPartnerSummaryRows([]);
                setHourlyTrendRows([]);
                setGlobalInvoiceTrendRows([]);
                setCountryDetailMetrics(null);
                setCountryDetailTopErrorMessages([]);
            }).finally(() => {
                setIsCountryDetailLoading(false);
            });
            return;
        }

        if (isCustomDateRangeIncomplete) {
            setIsCountryDetailLoading(false);
            return;
        }

        const request = toPartnerRequestForRange(
            selectedCountryCompanyCodes,
            selectedPeriodRange.start,
            selectedPeriodRange.end,
            selectedCountryCode || undefined
        );

        const cacheKey = getDashboardCacheKey();
        const normalizedCountryCode = selectedCountryCode.trim().toUpperCase();
        console.log('[useEffect single country] Request:', request, 'countryCodes:', selectedCountryCompanyCodes);
        if (!skipCacheForManualRefreshRef.current && normalizedCountryCode.length > 0) {
            const cacheEntry = readFreshDashboardCacheEntry(cacheKey);
            const cachedCountryDetail = cacheEntry?.snapshot?.countryDetailsByCode?.[normalizedCountryCode];
            if (cachedCountryDetail) {
                setPartnerSummaryRows(cachedCountryDetail.partnerSummaryRows ?? []);
                setHourlyTrendRows(cachedCountryDetail.hourlyTrendRows ?? []);
                setCountryDetailMetrics(cachedCountryDetail.countryDetailMetrics ?? null);
                setCountryDetailTopErrorMessages(cachedCountryDetail.countryDetailTopErrorMessages ?? []);
                setIsCountryDetailLoading(false);
                return;
            }
        }

        setIsCountryDetailLoading(true);

        getActivePartnerCountryDetail({
            variables: { request },
            fetchPolicy: 'network-only',
        }).then((countryDetailResponse) => {
            const detail = countryDetailResponse?.data?.eimExecutiveDashboardV2CountryDetail;
            const partnerRows = detail?.topPartners ?? [];
            const trendRows = detail?.hourlyTrend ?? [];
            const detailMetrics = detail?.metrics ?? null;
            const topErrorMessages = (detail?.topErrorMessages ?? []).slice(0, 4);

            console.log('[useEffect - single country] Fetched:', normalizedCountryCode, '- partners:', partnerRows.length, 'trend rows:', trendRows.length, 'metrics:', detailMetrics);

            setPartnerSummaryRows(partnerRows);
            setHourlyTrendRows(trendRows);
            setCountryDetailMetrics(detailMetrics);
            setCountryDetailTopErrorMessages(topErrorMessages);

            writeCountryDetailToDashboardCache(cacheKey, normalizedCountryCode, {
                partnerSummaryRows: partnerRows,
                hourlyTrendRows: trendRows,
                countryDetailMetrics: detailMetrics,
                countryDetailTopErrorMessages: topErrorMessages,
            });
        }).catch(() => {
            setPartnerSummaryRows([]);
            setHourlyTrendRows([]);
            setCountryDetailMetrics(null);
            setCountryDetailTopErrorMessages([]);
        }).finally(() => {
            setIsCountryDetailLoading(false);
        });
    }, [getActivePartnerCountryDetail, getDashboardCacheKey, isCustomDateRangeIncomplete, readFreshDashboardCacheEntry, selectedCountryCode, selectedCountryCompanyCodes, selectedPeriodRange.end, selectedPeriodRange.start, writeCountryDetailToDashboardCache]);

    const selectedTodayMetrics = useMemo(
        () => (selectedCountryCode.trim().length > 0 ? selectedCountryTodayMetrics : todayCountryMetrics),
        [selectedCountryCode, selectedCountryTodayMetrics, todayCountryMetrics]
    );

    const selectedYesterdayMetrics = useMemo(
        () => (selectedCountryCode.trim().length > 0 ? selectedCountryYesterdayMetrics : yesterdayCountryMetrics),
        [selectedCountryCode, selectedCountryYesterdayMetrics, yesterdayCountryMetrics]
    );

    const selectedTodayAggregate = useMemo(
        () => buildCountryMetricsAggregate(selectedTodayMetrics),
        [selectedTodayMetrics]
    );

    const selectedYesterdayAggregate = useMemo(
        () => buildCountryMetricsAggregate(selectedYesterdayMetrics),
        [selectedYesterdayMetrics]
    );

    const topMetrics = useMemo<TopMetricDefinition[]>(
        () => buildTopMetrics({
            intl,
            periodDisplayLabel,
            comparisonPeriodDisplayLabel,
            trendComparisonSuffix,
            selectedTodayMetrics,
            selectedYesterdayMetrics,
            selectedTodayAggregate,
            selectedYesterdayAggregate,
            globalInvoiceTrendRows,
            onMetricClick: navigateToOperationalDataWithFilters,
        }),
        [
            globalInvoiceTrendRows,
            intl,
            comparisonPeriodDisplayLabel,
            navigateToOperationalDataWithFilters,
            periodDisplayLabel,
            selectedTodayAggregate,
            selectedTodayMetrics,
            selectedYesterdayAggregate,
            selectedYesterdayMetrics,
            trendComparisonSuffix,
        ]
    );

    const worldMapSeriesData = useMemo<WorldMapSeriesPoint[]>(() => worldMapPoints.map((point) => {
        const totalProcessed = point.totalProcessed;
        const failedInvoiceCount = point.failedCount + point.overdueCount;
        const failureRate = totalProcessed > 0 ? (failedInvoiceCount / totalProcessed) * 100 : 0;
        const inTransitRate = totalProcessed > 0 ? (point.inTransitCount / totalProcessed) * 100 : 0;
        const successRate = Math.max(0, Math.min(100, point.stpRate));

        let healthCategory: WorldMapHealthCategory = 'noData';
        if (totalProcessed > 0) {
            const dominantRate = Math.max(failureRate, inTransitRate, successRate);
            if (dominantRate === failureRate) {
                healthCategory = 'critical';
            } else if (dominantRate === inTransitRate) {
                healthCategory = 'warning';
            } else {
                healthCategory = 'healthy';
            }
        }

        return {
            ...point,
            healthCategory,
            failureRate,
            inTransitRate,
            itemStyle: {
                areaColor: WORLD_MAP_HEALTH_COLORS[healthCategory],
            },
        };
    }), [worldMapPoints]);

    const mapOptions = useMemo(() => ({
        backgroundColor: 'transparent',
        tooltip: {
            trigger: 'item',
            backgroundColor: isLightTheme ? '#ffffff' : '#0f172a',
            borderColor: isLightTheme ? '#cbd5e1' : '#334155',
            textStyle: {
                color: isLightTheme ? '#0f172a' : '#f8fafc',
            },
            formatter: (params: any) => {
                const point = params?.data as WorldMapSeriesPoint | undefined;
                const healthCategory = point?.healthCategory ?? 'noData';
                const countryCode = String(point?.countryCode ?? '').trim().toUpperCase();
                const countryFlagUrl = getCountryFlagUrl(countryCode);
                const healthLabel = getI18n(
                    HEALTH_CATEGORY_I18N_KEY[healthCategory],
                    getHealthDefaultLabel(healthCategory),
                    intl
                );
                const healthColor = WORLD_MAP_HEALTH_COLORS[healthCategory];
                const totalProcessed = Number(point?.totalProcessed ?? 0);
                const successRate = Number(point?.stpRate ?? 0);
                const avgTransitDays = Number(point?.avgTransitDays ?? 0);
                const openErrors = Number(point?.failedCount ?? 0) + Number(point?.overdueCount ?? 0);

                return [
                    '<div style="min-width:230px;padding:2px 0;font-family:Arial,sans-serif;">',
                    '<div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:8px;">',
                    '<div style="display:flex;align-items:center;gap:8px;min-width:0;">',
                    countryFlagUrl
                        ? `<span style="display:inline-flex;align-items:center;justify-content:center;overflow:hidden;border-radius:999px;background:#f3f4f6;width:18px;height:18px;flex-shrink:0;"><img src="${countryFlagUrl}" alt="${escapePopupHtml(countryCode)}" width="18" height="18" style="display:block;width:18px;height:18px;object-fit:cover;" onerror="this.style.display='none';" /></span>`
                        : '',
                    `<strong style="font-size:14px;color:${isLightTheme ? '#0f172a' : '#f8fafc'};white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${params?.name ?? 'Unknown'}</strong>`,
                    '</div>',
                    `<span style="display:inline-flex;align-items:center;gap:6px;padding:2px 10px;border-radius:999px;background:${healthColor}22;color:${isLightTheme ? '#0f172a' : '#f8fafc'};font-size:12px;font-weight:600;">`,
                    `<span style="width:7px;height:7px;border-radius:50%;background:${healthColor};display:inline-block;"></span>${healthLabel}</span>`,
                    '</div>',
                    `<div style="display:flex;justify-content:space-between;gap:12px;padding:4px 0;color:${isLightTheme ? '#334155' : '#cbd5e1'};font-size:12px;"><span>${getI18n('eim-v2d-kpi-invoices-period', 'Invoices', intl)} (${periodDisplayLabel})</span><strong style="color:${isLightTheme ? '#0f172a' : '#f8fafc'};">${formatCount(totalProcessed, intl)}</strong></div>`,
                    `<div style="display:flex;justify-content:space-between;gap:12px;padding:4px 0;color:${isLightTheme ? '#334155' : '#cbd5e1'};font-size:12px;"><span>${getI18n('eim-v2d-kpi-success-rate', 'Success rate', intl)}</span><strong style="color:${isLightTheme ? '#0f172a' : '#f8fafc'};">${formatPercent(successRate, intl)}</strong></div>`,
                    `<div style="display:flex;justify-content:space-between;gap:12px;padding:4px 0;color:${isLightTheme ? '#334155' : '#cbd5e1'};font-size:12px;"><span>${getI18n('eim-v2d-kpi-avg-processing-time', 'Avg. processing time', intl)}</span><strong style="color:${isLightTheme ? '#0f172a' : '#f8fafc'};">${formatProcessingTime(avgTransitDays, intl)}</strong></div>`,
                    `<div style="display:flex;justify-content:space-between;gap:12px;padding:4px 0;color:#334155;font-size:12px;"><span>${getI18n('eim-v2d-kpi-open-errors', 'Open errors', intl)}</span><strong style="color:#dc2626;">${formatCount(openErrors, intl)}</strong></div>`,
                    '</div>',
                ].join('');
            },
        },
        series: [
            {
                name: getI18n('eim-world-map-series-name', 'Total invoices by country', intl),
                type: 'map',
                map: WORLD_MAP_NAME,
                roam: true,
                layoutCenter: ['50%', '48%'],
                layoutSize: '130%',
                emphasis: {
                    label: { show: false },
                    itemStyle: {
                        borderColor: isLightTheme ? '#1f2937' : '#f8fafc',
                        borderWidth: 1,
                    },
                },
                itemStyle: {
                    areaColor: WORLD_MAP_HEALTH_COLORS.noData,
                    borderColor: isLightTheme ? '#94a3b8' : '#64748b',
                    borderWidth: 0.6,
                },
                data: worldMapSeriesData,
            },
        ],
    }), [intl, isLightTheme, periodDisplayLabel, worldMapSeriesData]);

    const mapEvents = useMemo(() => ({
        click: (params: any) => {
            const countryCode = params?.data?.countryCode;
            if (countryCode) {
                    dispatch(setCountryCode(selectedCountryCode === countryCode ? '' : countryCode));
            }
        },
    }), []);

    const trendChartLabels = useMemo(() => {
        let labels: string[] = [];
        let trendByKey = new Map<string, { success: number; failed: number }>();

        // For 'today' period, use hourly labels (24 hours)
        // For other periods, use daily labels
        const isDaily = selectedPeriodOption !== 'today';

        if (isDaily) {
            // Daily labels: generate one label per day from start to end of period
            let currentDate = selectedPeriodRange.start;
            const endDate = selectedPeriodRange.end;
            
            while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
                labels.push(currentDate.format('MMM D'));
                currentDate = currentDate.add(1, 'day');
            }
        } else {
            // Hourly labels: generate 24 hours from now backwards
            labels = new Array<string>(24).fill('').map((_, index) => dayjs().subtract(23 - index, 'hour').format('HH:00'));
        }

        trendByKey = new Map(
            labels.map((label) => [label, { success: 0, failed: 0 }])
        );

        hourlyTrendRows.forEach((row) => {
            let normalizedLabel = '';
            
            if (isDaily) {
                // For daily aggregation, extract date from the hourLabel
                const rawLabel = String(row.hourLabel ?? '').trim();
                if (rawLabel) {
                    const parsed = dayjs(rawLabel);
                    if (parsed.isValid()) {
                        normalizedLabel = parsed.format('MMM D');
                    }
                }
            } else {
                // For hourly, use existing normalization
                normalizedLabel = normalizeHourlyBucketLabel(row.hourLabel);
            }

            if (!normalizedLabel || !trendByKey.has(normalizedLabel)) {
                return;
            }

            const current = trendByKey.get(normalizedLabel) ?? { success: 0, failed: 0 };

            trendByKey.set(normalizedLabel, {
                success: current.success + Math.max(0, Number(row.successfulCount ?? 0)),
                failed: current.failed + Math.max(0, Number(row.failedCount ?? 0)),
            });
        });

        return labels.map((label) => ({
            label,
            success: trendByKey.get(label)?.success ?? 0,
            failed: trendByKey.get(label)?.failed ?? 0,
        }));
    }, [hourlyTrendRows, selectedPeriodOption, selectedPeriodRange.start, selectedPeriodRange.end]);

    // Debug logging for trend data
    useMemo(() => {
        const totalSuccess = trendChartLabels.reduce((sum, item) => sum + item.success, 0);
        const totalFailed = trendChartLabels.reduce((sum, item) => sum + item.failed, 0);
        console.log(`Trend Chart - Period: ${selectedPeriodOption}, Raw rows: ${hourlyTrendRows.length}, Labels: ${trendChartLabels.length}, Total Success: ${totalSuccess}, Total Failed: ${totalFailed}`, hourlyTrendRows);
    }, [trendChartLabels, selectedPeriodOption, hourlyTrendRows]);

    const trendOptions = useMemo(() => ({
        grid: {
            left: 30,
            right: 16,
            top: 24,
            bottom: 24,
        },
        tooltip: {
            trigger: 'axis',
            formatter: (params: any) => {
                const entries = Array.isArray(params) ? params : [];
                const axisLabel = String(entries[0]?.axisValueLabel ?? entries[0]?.axisValue ?? '');
                const dataIndex = entries[0]?.dataIndex;

                const successEntry = entries.find((entry) => entry?.seriesIndex === 0);
                const failedEntry = entries.find((entry) => entry?.seriesIndex === 1);

                const successLabel = getI18n('eim-successful-label', 'Successful', intl);
                const failedLabel = getI18n('eim-failed-label', 'Failed', intl);
                const runningTotalLabel = getI18n('eim-running-total-label', 'Running total', intl);
                
                const successValue = Number(successEntry?.data ?? 0);
                const failedValue = Number(failedEntry?.data ?? 0);

                // Calculate running totals up to current data point
                let runningSuccessTotal = 0;
                let runningFailedTotal = 0;
                if (typeof dataIndex === 'number' && dataIndex >= 0) {
                    for (let i = 0; i <= dataIndex; i++) {
                        runningSuccessTotal += trendChartLabels[i]?.success ?? 0;
                        runningFailedTotal += trendChartLabels[i]?.failed ?? 0;
                    }
                }

                return [
                    `<div style="font-weight:600;margin-bottom:6px;">${axisLabel}</div>`,
                    `<div><span style="display:inline-block;margin-right:6px;border-radius:50%;width:8px;height:8px;background:${TREND_SUCCESS_COLOR}"></span>${successLabel}: ${formatCount(successValue, intl)}</div>`,
                    `<div><span style="display:inline-block;margin-right:6px;border-radius:50%;width:8px;height:8px;background:${TREND_FAILED_COLOR}"></span>${failedLabel}: ${formatCount(failedValue, intl)}</div>`,
                    `<div style="margin-top:8px;padding-top:8px;border-top:1px solid rgba(255,255,255,0.2);font-size:11px;color:${isLightTheme ? '#64748b' : '#cbd5e1'};">${runningTotalLabel}:</div>`,
                    `<div style="font-size:11px;color:${isLightTheme ? '#64748b' : '#cbd5e1'};"><span style="display:inline-block;margin-right:6px;border-radius:50%;width:6px;height:6px;background:${TREND_SUCCESS_COLOR}"></span>${successLabel}: ${formatCount(runningSuccessTotal, intl)}</div>`,
                    `<div style="font-size:11px;color:${isLightTheme ? '#64748b' : '#cbd5e1'};"><span style="display:inline-block;margin-right:6px;border-radius:50%;width:6px;height:6px;background:${TREND_FAILED_COLOR}"></span>${failedLabel}: ${formatCount(runningFailedTotal, intl)}</div>`,
                ].join('');
            },
        },
        xAxis: {
            type: 'category',
            data: trendChartLabels.map((item) => item.label),
            axisLabel: {
                color: isLightTheme ? '#64748b' : '#94a3b8',
                fontSize: 11,
            },
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                color: isLightTheme ? '#64748b' : '#94a3b8',
                fontSize: 11,
            },
            splitLine: {
                lineStyle: { color: isLightTheme ? '#e2e8f0' : '#334155' },
            },
        },
        series: [
            {
                name: getI18n('eim-successful-label', 'Successful', intl),
                type: 'line',
                smooth: true,
                showSymbol: false,
                lineStyle: { color: TREND_SUCCESS_COLOR, width: 2 },
                data: trendChartLabels.map((item) => item.success),
            },
            {
                name: getI18n('eim-failed-label', 'Failed', intl),
                type: 'line',
                smooth: true,
                showSymbol: false,
                lineStyle: { color: TREND_FAILED_COLOR, width: 2 },
                data: trendChartLabels.map((item) => item.failed),
            },
        ],
    }), [intl, isLightTheme, trendChartLabels]);

    const trendSectionTitle = useMemo(() => {
        const baseTrendLabel = getI18n('eim-v2d-processing-trend', 'Processing Trend', intl);
        let periodLabel = '';

        if (selectedPeriodOption === 'today') {
            periodLabel = getI18n('eim-period-today', 'Today', intl);
        } else if (selectedPeriodOption === 'currentWeek') {
            periodLabel = getI18n('eim-period-currentWeek', 'Current week', intl);
        } else if (selectedPeriodOption === 'currentMonth') {
            periodLabel = getI18n('eim-period-currentMonth', 'Current month', intl);
        } else if (selectedPeriodOption === 'custom' && customPeriodRange?.[0] && customPeriodRange?.[1]) {
            const startDate = customPeriodRange[0].format('MMM D');
            const endDate = customPeriodRange[1].format('MMM D');
            periodLabel = `${startDate} - ${endDate}`;
        }

        return periodLabel ? `${baseTrendLabel} (${periodLabel})` : baseTrendLabel;
    }, [intl, selectedPeriodOption, customPeriodRange]);

    const groupedPartnerSummaryRows = useMemo(() => {
        const groupedRows = new Map<string, { partnerno: string | null; partnerName: string | null; company: string | null; total: number; direction: string }>();

        partnerSummaryRows.forEach((partnerSummary) => {
            const partnerno = (partnerSummary.partnerno ?? '').trim() || null;
            const partnerName = (partnerSummary.partnerName ?? '').trim() || null;
            const company = (partnerSummary.company ?? '').trim() || null;
            const direction = partnerSummary.direction === 'IN' || partnerSummary.direction === 'OUT'
                ? partnerSummary.direction
                : 'UNKNOWN';
            const groupedKey = `${direction}|${partnerno ?? partnerName ?? 'UNKNOWN'}|${company ?? ''}`;
            const current = groupedRows.get(groupedKey);

            if (!current) {
                groupedRows.set(groupedKey, {
                    partnerno,
                    partnerName,
                    company,
                    total: Math.max(0, Number(partnerSummary.total ?? 0)),
                    direction,
                });
                return;
            }

            current.total += Math.max(0, Number(partnerSummary.total ?? 0));
        });

        const normalizedRows = Array.from(groupedRows.values())
            .sort((left, right) => right.total - left.total);
        const totalVolume = normalizedRows.reduce((sum, row) => sum + row.total, 0);

        return normalizedRows.map((row) => ({
            partnerno: row.partnerno,
            partnerName: row.partnerName,
            company: row.company,
            total: row.total,
            direction: row.direction,
            sharePercent: totalVolume > 0 ? (row.total / totalVolume) * 100 : 0,
        }));
    }, [partnerSummaryRows]);

    const topInboundPartners = useMemo(
        () => groupedPartnerSummaryRows.filter((row) => row.direction === 'IN').slice(0, 5),
        [groupedPartnerSummaryRows]
    );
    const topOutboundPartners = useMemo(
        () => groupedPartnerSummaryRows.filter((row) => row.direction === 'OUT').slice(0, 5),
        [groupedPartnerSummaryRows]
    );
    const allPartnerVolumeRows = useMemo<PartnerVolumeRow[]>(() => {
        const partnerRows = new Map<string, {
            partnerno: string | null;
            partnerName: string | null;
            company: string | null;
            inboundVolume: number;
            outboundVolume: number;
            unknownVolume: number;
        }>();

        groupedPartnerSummaryRows.forEach((partnerSummary) => {
            const partnerKey = `${partnerSummary.partnerno ?? partnerSummary.partnerName ?? 'UNKNOWN'}|${partnerSummary.company ?? ''}`;
            const currentRow = partnerRows.get(partnerKey) ?? {
                partnerno: partnerSummary.partnerno,
                partnerName: partnerSummary.partnerName,
                company: partnerSummary.company,
                inboundVolume: 0,
                outboundVolume: 0,
                unknownVolume: 0,
            };
            const volume = Math.max(0, Number(partnerSummary.total ?? 0));

            if (partnerSummary.direction === 'IN') {
                currentRow.inboundVolume += volume;
            } else if (partnerSummary.direction === 'OUT') {
                currentRow.outboundVolume += volume;
            } else {
                currentRow.unknownVolume += volume;
            }

            partnerRows.set(partnerKey, currentRow);
        });

        const normalizedRows = Array.from(partnerRows.values())
            .map((partnerSummary) => ({
                ...partnerSummary,
                totalVolume: partnerSummary.inboundVolume + partnerSummary.outboundVolume + partnerSummary.unknownVolume,
            }))
            .sort((left, right) => right.totalVolume - left.totalVolume);
        const totalVolume = normalizedRows.reduce((sum, row) => sum + row.totalVolume, 0);

        return normalizedRows.map((row) => ({
            ...row,
            sharePercent: totalVolume > 0 ? (row.totalVolume / totalVolume) * 100 : 0,
        }));
    }, [groupedPartnerSummaryRows]);

    const partnerVolumeColumnDefs = useMemo<ColDef<PartnerVolumeRow>[]>(() => [
        {
            headerName: '#',
            valueGetter: (params) => (params.node?.rowIndex ?? 0) + 1,
            width: 80,
            sortable: false,
        },
        {
            field: 'partnerName',
            headerName: getI18n('eim-country-details-all-partners-partner', 'Partner', intl),
            flex: 2,
            minWidth: 220,
        },
        {
            field: 'partnerno',
            headerName: getI18n('eim-country-details-all-partners-partner-number', 'Partner number', intl),
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'company',
            headerName: getI18n('eim-company', 'Company', intl),
            flex: 1,
            minWidth: 120,
        },
        {
            field: 'inboundVolume',
            headerName: getI18n('eim-country-details-all-partners-inbound-volume', 'Inbound volume', intl),
            type: 'numericColumn',
            flex: 1,
            minWidth: 150,
            sort: 'desc',
            valueFormatter: (params) => formatCount(Number(params.value ?? 0), intl),
        },
        {
            field: 'outboundVolume',
            headerName: getI18n('eim-country-details-all-partners-outbound-volume', 'Outbound volume', intl),
            type: 'numericColumn',
            flex: 1,
            minWidth: 150,
            valueFormatter: (params) => formatCount(Number(params.value ?? 0), intl),
        },
        {
            field: 'totalVolume',
            headerName: getI18n('eim-country-details-all-partners-volume', 'Total volume', intl),
            type: 'numericColumn',
            flex: 1,
            minWidth: 140,
            valueFormatter: (params) => formatCount(Number(params.value ?? 0), intl),
        },
        {
            field: 'sharePercent',
            headerName: getI18n('eim-v2d-countries-col-share', 'Share', intl),
            type: 'numericColumn',
            flex: 1,
            minWidth: 110,
            valueFormatter: (params) => formatPercent(Number(params.value ?? 0), intl),
        },
    ], [intl]);

    const countryTopErrorRows = useMemo(() => {
        if (countryDetailTopErrorMessages.length > 0) {
            const total = countryDetailTopErrorMessages.reduce((sum, row) => sum + Math.max(0, Number(row.count ?? 0)), 0);
            return countryDetailTopErrorMessages.slice(0, 4).map((row) => {
                const count = Math.max(0, Number(row.count ?? 0));
                const fallbackPercent = Math.round(Number(row.percentage ?? 0));
                const percent = total > 0 ? Math.round((count / total) * 100) : fallbackPercent;
                const message = (row.message ?? '').trim().length > 0
                    ? String(row.message).trim()
                    : getI18n('eim-country-details-error-unknown', 'Unknown', intl);

                return {
                    direction: row.direction === 'IN' || row.direction === 'OUT' ? row.direction : 'UNKNOWN',
                    message,
                    count,
                    percent,
                };
            });
        }

        return [];
    }, [countryDetailTopErrorMessages, intl]);

    const countryErrorTones = ['critical', 'warning', 'healthy', 'neutral'] as const;

    const countryErrorDonutTotal = useMemo(
        () => countryTopErrorRows.reduce((sum, item) => sum + item.count, 0),
        [countryTopErrorRows]
    );

    const countryErrorDonutSegments = useMemo(() => {
        const radius = 34;
        const circumference = 2 * Math.PI * radius;
        let cumulativeOffset = 0;

        return countryTopErrorRows.map((item, index) => {
            const fraction = countryErrorDonutTotal > 0 ? item.count / countryErrorDonutTotal : 0;
            const segmentLength = fraction * circumference;
            const visibleLength = Math.max(0, segmentLength - 1.2);
            const dashArray = `${visibleLength} ${circumference - visibleLength}`;
            const dashOffset = -cumulativeOffset;
            const tone = countryErrorTones[index % countryErrorTones.length];

            cumulativeOffset += segmentLength;

            return {
                ...item,
                dashArray,
                dashOffset,
                tone,
            };
        });
    }, [countryTopErrorRows, countryErrorDonutTotal]);

    const incidentRows = useMemo<IncidentRow[]>(() => globalTopErrorMessages
        .slice(0, 5)
        .map((errorRow) => {
            const count = Math.max(0, Number(errorRow.count ?? 0));
            const percentage = Math.max(0, Number(errorRow.percentage ?? 0));
            const severity: IncidentSeverity = percentage >= ERROR_FEED_CRITICAL_PERCENT_THRESHOLD ? 'critical' : 'warning';
            return {
                key: `${errorRow.direction ?? 'UNKNOWN'}-${errorRow.message ?? 'error'}`,
                severity,
                label: errorRow.message ?? getI18n('eim-v2d-incident-default', 'Processing anomaly detected', intl),
                detail: `${formatCount(count, intl)} ${getI18n('eim-v2d-open-errors-label', 'Errors', intl)} (${formatPercent(percentage, intl)})`,
            };
        }), [globalTopErrorMessages, intl]);

    const topCountryRows = useMemo<TopCountryRow[]>(() => {
        const ranked = [...worldMapPoints]
            .sort((left, right) => right.totalProcessed - left.totalProcessed)
            .slice(0, 5);
        const maxVolume = Math.max(1, ...ranked.map((row) => row.totalProcessed));

        return ranked.map((row, index) => ({
            ...row,
            rank: index + 1,
            progress: Math.max(8, Math.round((row.totalProcessed / maxVolume) * 100)),
        }));
    }, [worldMapPoints]);

    const systemVolumeRows = useMemo<VolumeLeaderboardRow[]>(() => {
        const bySystem = new Map<string, {
            code: string;
            name: string;
            totalProcessed: number;
            totalErrors: number;
        }>();

        systemMetrics.forEach((metric) => {
            const systemCode = String(metric.systemId ?? metric.systemLabel ?? '').trim().toUpperCase() || 'UNKNOWN';
            const current = bySystem.get(systemCode) ?? {
                code: systemCode,
                name: systemCode,
                totalProcessed: 0,
                totalErrors: 0,
            };

            current.totalProcessed += Math.max(0, Number(metric.intake?.totalProcessed ?? 0));
            current.totalErrors += Math.max(0, Number(metric.exceptions?.failedCount ?? 0))
                + Math.max(0, Number(metric.exceptions?.overdueCount ?? 0));
            bySystem.set(systemCode, current);
        });

        const rows = Array.from(bySystem.values())
            .sort((left, right) => right.totalProcessed - left.totalProcessed)
            .map((row) => ({
                code: row.code,
                name: row.name,
                totalProcessed: row.totalProcessed,
                totalErrors: row.totalErrors,
                errorRate: row.totalProcessed > 0 ? (row.totalErrors * 100) / row.totalProcessed : 0,
            }));

        const maxVolume = Math.max(1, ...rows.map((row) => row.totalProcessed));

        return rows.map((row, index) => ({
            ...row,
            rank: index + 1,
            progress: Math.max(8, Math.round((row.totalProcessed / maxVolume) * 100)),
        }));
    }, [systemMetrics]);

    const countryVolumeRows = useMemo<VolumeLeaderboardRow[]>(() => topCountryRows.map((row) => {
        const totalErrors = Number(row.failedCount ?? 0) + Number(row.overdueCount ?? 0);
        const errorRate = row.totalProcessed > 0 ? (totalErrors * 100) / row.totalProcessed : 0;
        return {
            rank: row.rank,
            code: row.countryCode,
            name: row.name,
            totalProcessed: row.totalProcessed,
            totalErrors,
            errorRate,
            progress: row.progress,
            countryCode: row.countryCode,
        };
    }), [topCountryRows]);

    const allCountryVolumeRows = useMemo<VolumeLeaderboardRow[]>(() => {
        // Collect unmapped records from countryMetrics (those filtered out of worldMapPoints)
        let unmappedTotal = 0;
        let unmappedErrors = 0;
        countryMetrics.forEach((countryMetric) => {
            const countryCode = resolveCountryCode(countryMetric, companyCountryLookup);
            if (countryCode.length === 2) {
                return; // This is mapped, skip
            }
            // This is unmapped/unresolved
            const totalProcessed = Number(countryMetric.intake?.totalProcessed ?? 0);
            const failedCount = Number(countryMetric.exceptions?.failedCount ?? 0);
            const overdueCount = Number(countryMetric.exceptions?.overdueCount ?? 0);
            unmappedTotal += totalProcessed;
            unmappedErrors += failedCount + overdueCount;
        });

        const rows = [...worldMapPoints]
            .sort((left, right) => right.totalProcessed - left.totalProcessed)
            .map((row, index) => {
                const totalErrors = Number(row.failedCount ?? 0) + Number(row.overdueCount ?? 0);
                const errorRate = row.totalProcessed > 0 ? (totalErrors * 100) / row.totalProcessed : 0;
                return {
                    rank: index + 1,
                    code: row.countryCode,
                    name: row.name,
                    totalProcessed: row.totalProcessed,
                    totalErrors,
                    errorRate,
                    progress: 0,
                    countryCode: row.countryCode,
                };
            });

        // Add unmapped row if there are unmapped records
        if (unmappedTotal > 0) {
            rows.push({
                rank: 0,
                code: 'UNMAPPED',
                name: getI18n('eim-unmapped-countries-label', 'Unmapped', intl),
                totalProcessed: unmappedTotal,
                totalErrors: unmappedErrors,
                errorRate: unmappedTotal > 0 ? (unmappedErrors * 100) / unmappedTotal : 0,
                progress: 0,
                countryCode: undefined,
            });
        }

        return rows;
    }, [worldMapPoints, countryMetrics, companyCountryLookup, intl]);

    const isCountrySelected = selectedCountryCode.trim().length > 0;
    const bottomDeckTitle = isCountrySelected
        ? getI18n('eim-v2d-top-systems-title', 'Volume by System', intl)
        : getI18n('eim-v2d-top-countries-title', 'Top Countries by Volume', intl);
    const bottomDeckModalTitle = isCountrySelected
        ? getI18n('eim-v2d-systems-modal-title', 'All Systems by Volume', intl)
        : getI18n('eim-v2d-countries-modal-title', 'All Countries by Volume', intl);
    const bottomDeckViewAllLabel = isCountrySelected
        ? getI18n('eim-v2d-view-all-systems', 'View all systems', intl)
        : getI18n('eim-v2d-view-all-countries', 'View all countries', intl);
    const bottomDeckNameColumnLabel = isCountrySelected
        ? getI18n('eim-v2d-countries-col-system', 'System', intl)
        : getI18n('eim-v2d-countries-col-country', 'Country', intl);
    const bottomDeckNameSortLabel = isCountrySelected
        ? getI18n('eim-v2d-countries-sort-system', 'System', intl)
        : getI18n('eim-v2d-countries-sort-country', 'Country', intl);

    const countryCodeByName = useMemo(() => {
        const lookup = new Map<string, string>();
        worldMapPoints.forEach((point) => {
            lookup.set(point.name.trim().toUpperCase(), point.countryCode.trim().toUpperCase());
        });
        return lookup;
    }, [worldMapPoints]);

    const allBmwGroupingRows = useMemo<VolumeLeaderboardRow[]>(() => {
        const normalizedSelectedCountryCode = selectedCountryCode.trim().toUpperCase();
        const rows = groupMetrics
            .filter((metric) => {
                if (!normalizedSelectedCountryCode) {
                    return true;
                }

                const rawMetricCountry = String(metric.country ?? '').trim();
                const normalizedRawMetricCountry = rawMetricCountry.toUpperCase();
                const metricCountryCode = normalizedRawMetricCountry.length === 2
                    ? normalizedRawMetricCountry
                    : (countryCodeByName.get(normalizedRawMetricCountry) ?? normalizedRawMetricCountry);
                return metricCountryCode === normalizedSelectedCountryCode;
            })
            .map((metric) => {
                const name = (metric.groupName ?? '').trim() || getI18n('eim-v2d-unknown-grouping', 'Unknown grouping', intl);
                const totalProcessed = Math.max(0, Number(metric.totalInvoices ?? 0));
                const totalErrors = Math.max(0, Number(metric.failedCount ?? 0));

                return {
                    code: name,
                    name,
                    totalProcessed,
                    totalErrors,
                };
            })
            .sort((left, right) => right.totalProcessed - left.totalProcessed)
            .map((row, index) => ({
                ...row,
                rank: index + 1,
                errorRate: row.totalProcessed > 0 ? (row.totalErrors * 100) / row.totalProcessed : 0,
            }));

        if (rows.length === 0) {
            return [];
        }

        const maxVolume = Math.max(1, ...rows.map((row) => row.totalProcessed));

        return rows.map((row) => ({
            ...row,
            progress: Math.max(8, Math.round((row.totalProcessed / maxVolume) * 100)),
        }));
    }, [countryCodeByName, groupMetrics, intl, selectedCountryCode]);

    const bmwGroupingRows = useMemo(
        () => allBmwGroupingRows.slice(0, 5),
        [allBmwGroupingRows]
    );

    const bmwGroupingTitle = getI18n('eim-v2d-top-bmw-groupings-title', 'Top BMW Groupings', intl);
    const bmwGroupingModalTitle = getI18n('eim-v2d-bmw-groupings-modal-title', 'All BMW Groupings by Volume', intl);
    const bmwGroupingViewAllLabel = getI18n('eim-v2d-view-all-bmw-groupings', 'View all groupings', intl);

    const systemStatusCategory = useMemo<WorldMapHealthCategory>(() => {
        const totalProcessed = todayMetricsAggregate.totalProcessed;
        const errorRate = totalProcessed > 0
            ? ((todayMetricsAggregate.failedCount + todayMetricsAggregate.overdueCount) * 100) / totalProcessed
            : 0;

        return deriveCountryHealthCategory({
            totalProcessed,
            errorRate,
            inTransitCount: todayMetricsAggregate.inTransitCount,
            successRate: todayMetricsAggregate.successRate,
        });
    }, [todayMetricsAggregate]);

    const systemStatusLabel = useMemo(() => getI18n(
        SYSTEM_STATUS_I18N_KEY[systemStatusCategory],
        getSystemStatusDefaultLabel(systemStatusCategory),
        intl
    ), [intl, systemStatusCategory]);

    const selectedCountryName = useMemo(() => {
        if (!selectedCountryCode) {
            return getI18n('eim-v2d-all-countries', 'All countries', intl);
        }

        const resolved = getMapName(selectedCountryCode, regionNames);
        return resolved ?? selectedCountryCode;
    }, [intl, regionNames, selectedCountryCode]);

    const selectableCountryOptions = useMemo(
        () => [...worldMapPoints]
            .sort((left, right) => left.name.localeCompare(right.name))
            .map((countryPoint) => ({
                value: countryPoint.countryCode,
                label: countryPoint.name,
            })),
        [worldMapPoints]
    );

    const detailsCountryOptions = useMemo(
        () => [
            {
                value: '',
                label: getI18n('eim-v2d-all-countries', 'All countries', intl),
            },
            ...selectableCountryOptions,
        ],
        [intl, selectableCountryOptions]
    );

    const hasMapData = worldMapPoints.length > 0;

    const exportPartnersToExcel = useCallback(async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(
            getI18n('eim-country-details-view-all-partners', 'View all partners', intl).substring(0, 31)
        );

        const partnerColumnLabel = getI18n('eim-country-details-all-partners-partner', 'Partner', intl);
        const partnerNumberColumnLabel = getI18n('eim-country-details-all-partners-partner-number', 'Partner number', intl);
        const companyColumnLabel = getI18n('eim-company', 'Company', intl);
        const inboundVolumeColumnLabel = getI18n('eim-country-details-all-partners-inbound-volume', 'Inbound volume', intl);
        const outboundVolumeColumnLabel = getI18n('eim-country-details-all-partners-outbound-volume', 'Outbound volume', intl);
        const totalVolumeColumnLabel = getI18n('eim-country-details-all-partners-volume', 'Total volume', intl);
        const shareColumnLabel = getI18n('eim-v2d-countries-col-share', 'Share', intl);

        worksheet.columns = [
            { header: '#', key: 'rank', width: 8 },
            { header: partnerColumnLabel, key: 'partnerName', width: 30 },
            { header: partnerNumberColumnLabel, key: 'partnerNumber', width: 18 },
            { header: companyColumnLabel, key: 'company', width: 15 },
            { header: inboundVolumeColumnLabel, key: 'inboundVolume', width: 15 },
            { header: outboundVolumeColumnLabel, key: 'outboundVolume', width: 15 },
            { header: totalVolumeColumnLabel, key: 'totalVolume', width: 15 },
            { header: shareColumnLabel, key: 'share', width: 12 },
        ];

        worksheet.getRow(1).font = { bold: true, color: { rgb: 'FFFFFFFF' } };
        worksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { rgb: 'FF0F172A' } };
        worksheet.getRow(1).alignment = { horizontal: 'center', vertical: 'center' };

        const sortedPartners = [...allPartnerVolumeRows];
        const unknownPartnerLabel = getI18n('eim-v2d-unknown-partner', 'Unknown partner', intl);

        sortedPartners.forEach((partner, index) => {
            const row = worksheet.addRow({
                rank: index + 1,
                partnerName: (partner.partnerName ?? '').trim() || unknownPartnerLabel,
                partnerNumber: (partner.partnerno ?? '').trim(),
                company: (partner.company ?? '').trim(),
                inboundVolume: partner.inboundVolume,
                outboundVolume: partner.outboundVolume,
                totalVolume: partner.totalVolume,
                share: `${formatPercent(Number(partner.sharePercent ?? 0), intl)}`,
            });

            ['inboundVolume', 'outboundVolume', 'totalVolume'].forEach((columnKey) => {
                row.getCell(columnKey).numFmt = '#,##0';
                row.getCell(columnKey).alignment = { horizontal: 'right' };
            });
            row.getCell('share').alignment = { horizontal: 'right' };

            if (index % 2 === 0) {
                row.fill = { type: 'pattern', pattern: 'solid', fgColor: { rgb: 'FFF8FAFC' } };
            }
        });

        const popupCountry = selectedCountryName.trim().length > 0 ? selectedCountryName : getI18n('eim-v2d-unknown-country', 'Unknown', intl);
        const timestamp = new Date().toISOString().split('T')[0];
        const fileName = `Partners_${popupCountry}_${timestamp}.xlsx`;

        await workbook.xlsx.writeBuffer().then((buffer: any) => {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        });
    }, [allPartnerVolumeRows, selectedCountryName, intl]);


        const openAllPartnersPopup = useCallback(() => {
                const popupWidth = 960;
                const popupHeight = 720;
                const currentWindow = globalThis.window;
                const activeScreen = currentWindow.screen;
                const screenWorkArea = activeScreen as Screen & { availLeft?: number; availTop?: number };
                const screenLeft = Number(screenWorkArea.availLeft ?? currentWindow.screenX ?? currentWindow.screenLeft ?? 0);
                const screenTop = Number(screenWorkArea.availTop ?? currentWindow.screenY ?? currentWindow.screenTop ?? 0);
                const availableWidth = Number(activeScreen.availWidth || currentWindow.innerWidth);
                const availableHeight = Number(activeScreen.availHeight || currentWindow.innerHeight);
                const left = Math.max(
                    screenLeft,
                    Math.round(screenLeft + ((availableWidth - popupWidth) / 2))
                );
                const top = Math.max(
                    screenTop,
                    Math.round(screenTop + ((availableHeight - popupHeight) / 2))
                );
                const popup = currentWindow.open(
                    '',
                    '_blank',
                    `width=${popupWidth},height=${popupHeight},left=${left},top=${top},resizable=yes,scrollbars=yes`
                );
                if (!popup) {
                        return;
                }
                popup.moveTo(left, top);
                popup.focus();

                const sortedPartners = [...allPartnerVolumeRows];
                
                const popupTitlePrefix = getI18n('eim-country-details-view-all-partners', 'View all partners', intl);
                const popupCountry = selectedCountryName.trim().length > 0 ? selectedCountryName : getI18n('eim-v2d-unknown-country', 'Unknown', intl);
                const partnerColumnLabel = getI18n('eim-country-details-all-partners-partner', 'Partner', intl);
                const partnerNumberColumnLabel = getI18n('eim-country-details-all-partners-partner-number', 'Partner number', intl);
                const companyColumnLabel = getI18n('eim-company', 'Company', intl);
                const inboundVolumeColumnLabel = getI18n('eim-country-details-all-partners-inbound-volume', 'Inbound volume', intl);
                const outboundVolumeColumnLabel = getI18n('eim-country-details-all-partners-outbound-volume', 'Outbound volume', intl);
                const totalVolumeColumnLabel = getI18n('eim-country-details-all-partners-volume', 'Total volume', intl);
                const shareColumnLabel = getI18n('eim-v2d-countries-col-share', 'Share', intl);
                const unknownPartnerLabel = getI18n('eim-v2d-unknown-partner', 'Unknown partner', intl);
                const emptyStateLabel = getI18n('eim-country-details-all-partners-empty', 'No partner volume data available.', intl);
                const popupTitle = `${popupTitlePrefix} - ${popupCountry}`;
                const rowsHtml = sortedPartners.length > 0
                        ? sortedPartners
                                .map((partnerSummary, index) => {
                                        const rank = index + 1;
                                        const partnerName = escapePopupHtml((partnerSummary.partnerName ?? '').trim() || unknownPartnerLabel);
                                        const partnerNumber = escapePopupHtml((partnerSummary.partnerno ?? '').trim());
                                        const companyCode = escapePopupHtml((partnerSummary.company ?? '').trim());
                                        const formattedInboundVolume = escapePopupHtml(formatCount(partnerSummary.inboundVolume, intl));
                                        const formattedOutboundVolume = escapePopupHtml(formatCount(partnerSummary.outboundVolume, intl));
                                        const formattedTotalVolume = escapePopupHtml(formatCount(partnerSummary.totalVolume, intl));
                            const formattedShare = escapePopupHtml(formatPercent(Number(partnerSummary.sharePercent ?? 0), intl));
                                        return `<tr><td>${rank}</td><td>${partnerName}</td><td>${partnerNumber}</td><td>${companyCode}</td><td style="text-align:right;">${formattedInboundVolume}</td><td style="text-align:right;">${formattedOutboundVolume}</td><td style="text-align:right;">${formattedTotalVolume}</td><td style="text-align:right;">${formattedShare}</td></tr>`;
                                })
                                .join('')
                                    : `<tr><td colspan="8" style="text-align:center;color:#64748b;">${escapePopupHtml(emptyStateLabel)}</td></tr>`;

                const popupHtml = `<!doctype html>
<html lang="en">
<head>
    <meta charset="utf-8" />
    <title>${escapePopupHtml(popupTitle)}</title>
    <style>
        body { font-family: "Segoe UI", Arial, sans-serif; margin: 20px; color: #0f172a; background: #f8fafc; }
        h1 { margin: 0 0 12px; font-size: 20px; }
        .toolbar { margin-bottom: 16px; }
        .export-button { 
            padding: 8px 16px; 
            background-color: #1890ff; 
            color: white; 
            border: none; 
            border-radius: 4px; 
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
        }
        .export-button:hover { background-color: #40a9ff; }
        .export-button:active { background-color: #096dd9; }
        table { width: 100%; border-collapse: collapse; background: #ffffff; margin-top: 12px; }
        th, td { border: 1px solid #e2e8f0; padding: 8px 10px; }
        th { background: #f1f5f9; text-align: left; font-weight: 600; }
        tr:nth-child(even) td { background: #f8fafc; }
    </style>
</head>
<body>
    <div>
        <h1>${escapePopupHtml(popupTitle)}</h1>
        <div class="toolbar">
            <button class="export-button" id="exportBtn">${escapePopupHtml(getI18n('export-to-excel', 'Export to Excel', intl))}</button>
        </div>
    </div>
    <table>
        <thead>
            <tr>
                <th>#</th>
                <th>${escapePopupHtml(partnerColumnLabel)}</th>
                <th>${escapePopupHtml(partnerNumberColumnLabel)}</th>
                <th>${escapePopupHtml(companyColumnLabel)}</th>
                <th style="text-align:right;">${escapePopupHtml(inboundVolumeColumnLabel)}</th>
                <th style="text-align:right;">${escapePopupHtml(outboundVolumeColumnLabel)}</th>
                <th style="text-align:right;">${escapePopupHtml(totalVolumeColumnLabel)}</th>
                <th style="text-align:right;">${escapePopupHtml(shareColumnLabel)}</th>
            </tr>
        </thead>
        <tbody>
            ${rowsHtml}
        </tbody>
    </table>
    <script>
        document.getElementById('exportBtn').addEventListener('click', function() {
            if (window.opener && window.opener.__eimDashboardExportPartnersToExcel) {
                window.opener.__eimDashboardExportPartnersToExcel();
            }
        });
    </script>
</body>
</html>`;

        // Use document.write for more reliable popup content injection
        popup.document.open();
        popup.document.write(popupHtml);
        popup.document.close();
        }, [allPartnerVolumeRows, intl, selectedCountryName]);

    useEffect(() => {
        // Expose the export function to the window so popups can call it via window.opener
        (globalThis.window as any).__eimDashboardExportPartnersToExcel = exportPartnersToExcel;

        return () => {
            // Clean up the reference when component unmounts
            delete (globalThis.window as any).__eimDashboardExportPartnersToExcel;
        };
    }, [exportPartnersToExcel]);

    return (
        <div className={getPageClassName(styles, theme)}>
            <div className={getDashboardShellClassName(styles, theme)}>
                <div className={styles.pageThemeToolbar}>
                    <Button type="link" className={styles.refreshButton} onClick={() => navigate('/cfo-reporting-dashboard')}>
                        {getI18n('cfo-reporting-shortcut', 'CFO Reporting', intl)}
                    </Button>
                    <Button type="link" className={styles.refreshButton} onClick={() => navigate('/cfo-compliance-report')}>
                        {getI18n('cfo-compliance-shortcut', 'CFO Compliance Report', intl)}
                    </Button>
                    <Button className={styles.pageThemeToggle} icon={<BulbOutlined />} onClick={handleToggleTheme}>
                        {theme === 'dark'
                            ? getI18n('eim-switch-to-light-view', 'Switch to light view', intl)
                            : getI18n('eim-switch-to-dark-view', 'Switch to dark view', intl)}
                    </Button>
                </div>

                <header className={styles.header}>
                    <div>
                        <h1>{getI18n('eim-v2d-title', 'E2E Monitor', intl)}</h1>
                        <p>{getI18n('eim-v2d-subtitle', 'End-to-End Invoice Monitoring', intl)}</p>
                    </div>
                    <div className={styles.headerControls}>
                        <div className={styles.periodControls}>
                            <Select
                                size="small"
                                value={selectedPeriodOption}
                                onChange={(value: PeriodOption) => dispatch(setPeriodOption(value))}
                                options={[
                                    { value: 'today', label: getI18n('eim-period-today', 'Today', intl) },
                                    { value: 'currentWeek', label: getI18n('eim-period-currentWeek', 'Current week', intl) },
                                    { value: 'currentMonth', label: getI18n('eim-period-currentMonth', 'Current month', intl) },
                                    { value: 'currentQuarter', label: getI18n('eim-period-currentQuarter', 'Current quarter', intl) },
                                    { value: 'currentYear', label: getI18n('eim-period-currentYear', 'Current year', intl) },
                                    { value: 'custom', label: getI18n('eim-period-custom', 'Custom date', intl) },
                                ]}
                                className={styles.periodSelect}
                            />
                            {selectedPeriodOption === 'custom' ? (
                                <RangePicker
                                    size="small"
                                    value={customPeriodRange ?? undefined}
                                    onChange={(value) => {
                                        if (value?.[0] && value?.[1]) {
                                            dispatch(setCustomPeriodRange({
                                                start: value[0].toISOString(),
                                                end: value[1].toISOString(),
                                            }));
                                        }
                                    }}
                                    allowClear={false}
                                />
                            ) : null}
                            <Tooltip title={getI18n('eim-v2d-documentation-button', 'Dashboard documentation', intl)}>
                                <Button
                                    type="link"
                                    size="small"
                                    icon={<InfoCircleOutlined />}
                                    aria-label={getI18n('eim-v2d-documentation-button', 'Dashboard documentation', intl)}
                                    onClick={() => setDocumentationModalOpen(true)}
                                />
                            </Tooltip>
                        </div>
                        <div className={styles.lastUpdated}>
                            {getI18n('eim-v2d-last-updated', 'Last update', intl)}: {dayjs().format('HH:mm:ss')} UTC
                            <Button
                                type="link"
                                icon={<ReloadOutlined />}
                                loading={isAnyDashboardRequestLoading}
                                disabled={isAnyDashboardRequestLoading}
                                onClick={() => {
                                    refreshStartedAtRef.current = Date.now();
                                    skipCacheForManualRefreshRef.current = true;
                                    setIsRefreshing(true);
                                    loadDashboard(true).catch(() => undefined);
                                }}
                                className={styles.refreshButton}
                            >
                                {getI18n('eim-refresh-overview', 'Refresh overview', intl)}
                            </Button>
                            <Spin spinning={isRefreshing} size="small" />
                        </div>
                    </div>
                </header>

                <Modal
                    title={getI18n('eim-v2d-documentation-title', 'E2E Monitoring Dashboard documentation', intl)}
                    open={documentationModalOpen}
                    onCancel={() => setDocumentationModalOpen(false)}
                    footer={null}
                    width={720}
                >
                    <p>{getI18n('eim-v2d-documentation-intro', 'The dashboard summarizes invoice flow, processing health, and open exceptions for the selected period.', intl)}</p>
                    <dl>
                        {[
                            ['invoices', 'eim-v2d-documentation-invoices-title', 'eim-v2d-documentation-invoices-description'],
                            ['flow', 'eim-v2d-documentation-flow-title', 'eim-v2d-documentation-flow-description'],
                            ['success', 'eim-v2d-documentation-success-title', 'eim-v2d-documentation-success-description'],
                            ['processing', 'eim-v2d-documentation-processing-title', 'eim-v2d-documentation-processing-description'],
                            ['countries', 'eim-v2d-documentation-countries-title', 'eim-v2d-documentation-countries-description'],
                            ['errors', 'eim-v2d-documentation-errors-title', 'eim-v2d-documentation-errors-description'],
                            ['transit', 'eim-v2d-documentation-transit-title', 'eim-v2d-documentation-transit-description'],
                            ['overdue', 'eim-v2d-documentation-overdue-title', 'eim-v2d-documentation-overdue-description'],
                        ].map(([, titleKey, descriptionKey]) => (
                            <React.Fragment key={titleKey}>
                                <dt><strong>{getI18n(titleKey, titleKey, intl)}</strong></dt>
                                <dd>{getI18n(descriptionKey, descriptionKey, intl)}</dd>
                            </React.Fragment>
                        ))}
                    </dl>
                    <p>{getI18n('eim-v2d-documentation-comparison', 'Comparison cards show the selected period versus its previous equivalent: yesterday, previous week, previous month, previous quarter, previous year, or the preceding custom range.', intl)}</p>
                </Modal>

                <Spin spinning={isAnyDashboardRequestLoading}>
                <section className={styles.kpiGrid}>
                {topMetrics.map((metric) => {
                    const sparklineData = metric.sparklineData ?? [];
                    const showSparkline = sparklineData.length > 1;
                    const trendClassName = metric.trendTone === 'positive' ? styles.trendPositive : styles.trendNegative;
                    const miniVisualData = metric.miniVisualData ?? [];
                    const miniVisualValue = Math.max(0, Math.min(100, Number(metric.miniVisualValue ?? 0)));
                    const ringOffset = 100 - miniVisualValue;

                    let miniVisual: React.ReactNode = null;
                    if (metric.miniVisualKind === 'ring') {
                        miniVisual = (
                            <svg className={styles.kpiRing} viewBox="0 0 40 40" aria-hidden="true">
                                <circle className={styles.kpiRingTrack} cx="20" cy="20" r="16" />
                                <circle
                                    className={styles.kpiRingValue}
                                    cx="20"
                                    cy="20"
                                    r="16"
                                    pathLength={100}
                                    strokeDasharray="100"
                                    strokeDashoffset={ringOffset}
                                />
                            </svg>
                        );
                    } else if (metric.miniVisualKind === 'line' && miniVisualData.length > 1) {
                        miniVisual = (
                            <svg className={styles.kpiMiniLine} viewBox="0 0 84 28" preserveAspectRatio="none" aria-hidden="true">
                                <polyline
                                    className={styles.kpiMiniLineStroke}
                                    points={getSparklinePolylinePoints(miniVisualData, 84, 28)}
                                />
                            </svg>
                        );
                    } else if (metric.miniVisualKind === 'bars' && miniVisualData.length > 0) {
                        const maxBarValue = Math.max(1, ...miniVisualData);
                        const barCount = miniVisualData.length;
                        const chartWidth = 84;
                        const chartHeight = 28;
                        const barGap = 2;
                        const barWidth = (chartWidth - ((barCount - 1) * barGap)) / Math.max(1, barCount);
                        miniVisual = (
                            <svg
                                className={styles.kpiMiniBarsSvg}
                                viewBox="0 0 84 28"
                                preserveAspectRatio="none"
                                aria-hidden="true"
                            >
                                {miniVisualData.map((barValue, index) => {
                                    const normalizedHeight = Math.max(2, Math.round((Math.max(0, barValue) / maxBarValue) * chartHeight));
                                    const x = index * (barWidth + barGap);
                                    const y = chartHeight - normalizedHeight;

                                    return (
                                        <rect
                                            key={`${metric.key}-bar-${index}`}
                                            className={styles.kpiMiniBarRect}
                                            x={x}
                                            y={y}
                                            width={barWidth}
                                            height={normalizedHeight}
                                            rx={1}
                                            ry={1}
                                        />
                                    );
                                })}
                            </svg>
                        );
                    } else if (metric.miniVisualKind === 'progress') {
                        miniVisual = (
                            <svg
                                className={styles.kpiMiniProgressSvg}
                                viewBox="0 0 84 8"
                                preserveAspectRatio="none"
                                aria-hidden="true"
                            >
                                <rect className={styles.kpiMiniProgressTrackRect} x={0} y={0} width={84} height={8} rx={4} ry={4} />
                                <rect
                                    className={styles.kpiMiniProgressFillRect}
                                    x={0}
                                    y={0}
                                    width={(84 * miniVisualValue) / 100}
                                    height={8}
                                    rx={4}
                                    ry={4}
                                />
                            </svg>
                        );
                    } else if (showSparkline) {
                        miniVisual = (
                            <svg className={styles.kpiSparkline} viewBox="0 0 84 28" preserveAspectRatio="none" aria-hidden="true">
                                <polyline
                                    className={styles.kpiSparklineStroke}
                                    points={getSparklinePolylinePoints(sparklineData, 84, 28)}
                                />
                            </svg>
                        );
                    }

                    return (
                        <Card 
                            key={metric.key} 
                            className={classNames(styles.kpiCard, { [styles.kpiCardClickable]: metric.isClickable })}
                            variant="borderless"
                            onClick={metric.onClick}
                            role={metric.isClickable ? 'button' : undefined}
                            tabIndex={metric.isClickable ? 0 : undefined}
                            onKeyDown={metric.isClickable ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    metric.onClick?.();
                                }
                            } : undefined}
                        >
                            <div className={styles.kpiTopRow}>
                                <div>
                                    <div className={styles.kpiLabel}>
                                        <span>{getI18n(metric.key, metric.key, intl)}</span>
                                        <Tooltip title={getI18n(metric.infoKey, metric.key, intl)} placement="topLeft">
                                            <Button
                                                type="link"
                                                size="small"
                                                icon={<InfoCircleOutlined />}
                                                aria-label={getI18n(metric.infoKey, metric.key, intl)}
                                                onClick={(event) => event.stopPropagation()}
                                            />
                                        </Tooltip>
                                    </div>
                                    <div className={styles.kpiValue}>{metric.value()}</div>
                                </div>
                                {miniVisual}
                            </div>
                            <div className={trendClassName}>
                                <Tooltip title={metric.hint?.() ?? metric.trend()} placement="topLeft">
                                    <span className={styles.trendContent}>
                                        {getTrendArrowIcon(metric.showTrendArrow, metric.trendTone, styles.trendArrow)}
                                        <span>{metric.trend()}</span>
                                    </span>
                                </Tooltip>
                            </div>
                        </Card>
                    );
                })}
                </section>

                <section className={getMainGridClassName(styles)}>
                <Card className={styles.mapCard} variant="borderless">
                    <div className={styles.sectionTitle}>{getI18n('eim-v2d-global-overview', 'Global Overview', intl)} ({periodDisplayLabel})</div>
                    <div className={styles.mapMetaIndicator}>
                        <span>{getI18n('eim-v2d-unmapped-country-volume', 'Unmapped country volume', intl)}:</span>
                        <strong>{formatCount(unmappedCountryVolume, intl)}</strong>
                    </div>
                    <div className={styles.mapMetaIndicator}>
                        <span>{getI18n('eim-v2d-unmapped-failures', 'Unmapped failures', intl)}:</span>
                        <Tooltip title={unmappedFailureTooltip} placement="topLeft">
                            <strong>{formatCount(unmappedFailureTotal, intl)}</strong>
                        </Tooltip>
                    </div>
                    <div className={getMapAreaClassName(styles)}>
                        {isLoading ? (
                            <div className={styles.mapCentered}><Spin /></div>
                        ) : null}
                        {!isLoading && hasMapData ? (
                            <ReactEcharts option={mapOptions} style={{ height: '100%', width: '100%' }} onEvents={mapEvents} />
                        ) : null}
                        {!isLoading && !hasMapData ? (
                            <div className={styles.mapCentered}>
                                <Empty description={getI18n('eim-v2d-no-map-data', 'No country metrics available for this period.', intl)} />
                            </div>
                        ) : null}
                    </div>
                </Card>

                <Card className={classNames(styles.detailsCard, isLightTheme ? styles.detailsCardLight : styles.detailsCardDark)} variant="borderless">
                    <Spin spinning={isCountryDetailLoading}>
                        <div className={styles.detailsTopRow}>
                            <div className={styles.countryHeader}>
                                {selectedCountryCode ? (
                                    <CountryFlagLabel countryCode={selectedCountryCode} label={<h2>{selectedCountryName}</h2>} flagSize={24} />
                                ) : (
                                    <h2>{selectedCountryName}</h2>
                                )}
                                <span
                                    className={[
                                        styles.healthyTag,
                                        selectedCountryHealthCategory === 'warning' ? styles.healthTagWarning : '',
                                        selectedCountryHealthCategory === 'critical' ? styles.healthTagCritical : '',
                                        selectedCountryHealthCategory === 'noData' ? styles.healthTagNoData : '',
                                    ].join(' ').trim()}
                                >
                                    {selectedCountryHealthLabel}
                                </span>
                            </div>
                            <div className={styles.countrySelectorWrap}>
                                <span>{getI18n('eim-v2d-select-country-label', 'Select country', intl)}</span>
                                <Select
                                    size="small"
                                    showSearch={true}
                                    className={styles.countrySelect}
                                    value={selectedCountryCode}
                                    options={detailsCountryOptions}
                                    optionFilterProp="label"
                                    onChange={(value) => dispatch(setCountryCode(String(value ?? '')))}
                                />
                            </div>
                        </div>

                        <div className={styles.trendCard}>
                            <div className={styles.sectionTitle}>{trendSectionTitle}</div>
                            <ReactEcharts option={trendOptions} style={{ height: 160, width: '100%' }} />
                            <div className={styles.trendLegend}>
                                <span className={styles.successLegend}>{getI18n('eim-successful-label', 'Successful', intl)}</span>
                                <span className={styles.failedLegend}>{getI18n('eim-failed-label', 'Failed', intl)}</span>
                            </div>
                        </div>

                        <div className={styles.bottomRow}>
                            <Card className={styles.infoCard} variant="borderless">
                                <div className={styles.sectionTitle}>{getI18n('eim-v2d-country-mix', 'Errors by type', intl)}</div>
                                {countryTopErrorRows.length > 0 ? (
                                    <div className={styles.countryErrorLayout}>
                                        <div className={styles.countryErrorSummary}>
                                            <svg viewBox="0 0 84 84" className={styles.countryErrorDonut} aria-hidden="true">
                                                <circle cx="42" cy="42" r="34" fill="none" stroke="rgba(149, 165, 192, 0.15)" strokeWidth="5" />
                                                {countryErrorDonutSegments.map((item, index) => (
                                                    <circle
                                                        key={`${item.message}-${item.count}`}
                                                        cx="42"
                                                        cy="42"
                                                        r="34"
                                                        fill="none"
                                                        stroke={getCountryErrorStrokeColor(item.tone)}
                                                        strokeWidth="10"
                                                        strokeDasharray={item.dashArray}
                                                        strokeDashoffset={item.dashOffset}
                                                        strokeLinecap="round"
                                                        className={classNames(
                                                            styles.countryErrorDonutSegment,
                                                            hoveredDonutSegment === null || hoveredDonutSegment === index ? '' : styles.countryErrorDonutSegmentMuted,
                                                            hoveredDonutSegment === index ? styles.countryErrorDonutSegmentActive : ''
                                                        )}
                                                        onMouseEnter={() => setHoveredDonutSegment(index)}
                                                        onMouseLeave={() => setHoveredDonutSegment(null)}
                                                    />
                                                ))}
                                            </svg>
                                            <div className={styles.countryErrorTotal}>
                                                {hoveredDonutSegment === null ? (
                                                    <>
                                                        <strong>{formatCount(countryErrorDonutTotal, intl)}</strong>
                                                        <div className={styles.countryErrorTotalLabel}>{getI18n('eim-country-details-total', 'Total', intl)}</div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <strong>{formatCount(countryErrorDonutSegments[hoveredDonutSegment].count, intl)}</strong>
                                                        <div className={styles.countryErrorTotalLabel}>{formatPercent(countryErrorDonutSegments[hoveredDonutSegment].percent, intl)}</div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <ul className={styles.countryErrorList}>
                                            {(['IN', 'OUT'] as const).map((direction) => {
                                                const directionRows = countryErrorDonutSegments.filter((item) => item.direction === direction);
                                                return (
                                                    <li key={direction} className={styles.countryErrorDirectionGroup}>
                                                        <strong>{getI18n(`eim-country-details-errors-${direction.toLowerCase()}`, direction === 'IN' ? 'Inbound errors' : 'Outbound errors', intl)}</strong>
                                                        {directionRows.length > 0 ? directionRows.map((item) => {
                                                            const itemIndex = countryErrorDonutSegments.indexOf(item);
                                                            return (
                                                                <div
                                                                    key={`${item.message}-${item.count}`}
                                                                    className={classNames(
                                                                        styles.countryErrorListItem,
                                                                        hoveredDonutSegment === null || hoveredDonutSegment === itemIndex ? '' : styles.countryErrorListItemMuted
                                                                    )}
                                                                    onMouseEnter={() => setHoveredDonutSegment(itemIndex)}
                                                                    onMouseLeave={() => setHoveredDonutSegment(null)}
                                                                >
                                                                    <span className={styles.countryErrorMessage}>
                                                                        <span className={classNames(styles.countryErrorDot, {
                                                                            [styles.countryErrorDotCritical]: item.tone === 'critical',
                                                                            [styles.countryErrorDotWarning]: item.tone === 'warning',
                                                                            [styles.countryErrorDotHealthy]: item.tone === 'healthy',
                                                                            [styles.countryErrorDotNeutral]: item.tone === 'neutral',
                                                                        })} />
                                                                        <span className={styles.countryErrorText} title={item.message}>{item.message}</span>
                                                                    </span>
                                                                    <strong>{formatCount(item.count, intl)} ({formatPercent(item.percent, intl)})</strong>
                                                                </div>
                                                            );
                                                        }) : <span className={styles.countryErrorDirectionEmpty}>{getI18n('eim-country-details-no-direction-error-data', 'No errors', intl)}</span>}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                ) : <Empty description={getI18n('eim-country-details-no-error-type-data', 'No error type data available for selected country.', intl)} />}
                                <Button type="link" onClick={() => navigateToOperationalDataWithFilters({ statusFilters: ['FAIL', 'OVERDUE'] })}>
                                    {getI18n('eim-v2d-view-all-incidents', 'View all errors', intl)} <ArrowRightOutlined />
                                </Button>
                            </Card>

                            <Card className={styles.infoCard} variant="borderless">
                                <div className={styles.sectionTitle}>{getI18n('eim-country-details-top-partners', 'Top Partners (by Volume)', intl)}</div>
                                {(['IN', 'OUT'] as const).map((direction) => {
                                    const partners = direction === 'IN' ? topInboundPartners : topOutboundPartners;
                                    return (
                                        <div key={direction}>
                                            <strong>{getI18n(`eim-country-details-partners-${direction.toLowerCase()}`, direction === 'IN' ? 'Inbound partners' : 'Outbound partners', intl)}</strong>
                                            <ul className={styles.simpleList}>
                                                {partners.map((partnerSummary) => (
                                                    <li key={`${direction}-${partnerSummary.partnerno}-${partnerSummary.company}`}>
                                                        <span>
                                                            {partnerSummary.partnerName ?? getI18n('eim-v2d-unknown-partner', 'Unknown partner', intl)}
                                                            {partnerSummary.partnerno ? ` (${partnerSummary.partnerno})` : ''}
                                                        </span>
                                                        <strong>
                                                            {formatCount(Number(partnerSummary.total ?? 0), intl)} ({formatPercent(Number(partnerSummary.sharePercent ?? 0), intl)})
                                                        </strong>
                                                    </li>
                                                ))}
                                                {partners.length === 0 ? <li>{getI18n('eim-v2d-no-partner-data', 'No partner data for selected country.', intl)}</li> : null}
                                            </ul>
                                        </div>
                                    );
                                })}
                                <Button type="link" className={styles.linkButton} onClick={() => setPartnersModalOpen(true)}>
                                    {getI18n('eim-country-details-view-all-partners', 'View all partners', intl)} <ArrowRightOutlined />
                                </Button>
                            </Card>
                        </div>
                        <Modal
                            title={`${getI18n('eim-country-details-all-partners-modal-title-prefix', 'All Partners by Volume', intl)} (${selectedCountryName})`}
                            open={partnersModalOpen}
                            onCancel={() => setPartnersModalOpen(false)}
                            width={1280}
                            footer={[
                                <Button key="export" type="primary" onClick={exportPartnersToExcel}>
                                    {getI18n('export-to-excel', 'Export to Excel', intl)}
                                </Button>,
                            ]}
                            destroyOnHidden={true}
                        >
                            <div className="ag-theme-balham" style={{ width: '100%', height: 560 }}>
                                <AgGridReact<PartnerVolumeRow>
                                    columnDefs={partnerVolumeColumnDefs}
                                    defaultColDef={{
                                        sortable: true,
                                        filter: true,
                                        resizable: true,
                                    }}
                                    rowData={allPartnerVolumeRows}
                                    pagination={true}
                                    paginationPageSize={25}
                                   // paginationPageSizeSelector={[25, 50, 100]}
                                    suppressCellFocus={true}
                                />
                            </div>
                        </Modal>
                    </Spin>
                </Card>
                </section>

                <ExecutiveV2BottomDeck
                    intl={intl}
                    navigate={navigate}
                    theme={theme}
                    systemStatusCategory={systemStatusCategory}
                    systemStatusLabel={systemStatusLabel}
                    incidentRows={incidentRows}
                    leaderboardRows={isCountrySelected ? systemVolumeRows.slice(0, 5) : countryVolumeRows}
                    allLeaderboardRows={isCountrySelected ? systemVolumeRows : allCountryVolumeRows}
                    leaderboardTitle={bottomDeckTitle}
                    leaderboardModalTitle={bottomDeckModalTitle}
                    leaderboardViewAllLabel={bottomDeckViewAllLabel}
                    leaderboardNameColumnLabel={bottomDeckNameColumnLabel}
                    leaderboardNameSortLabel={bottomDeckNameSortLabel}
                    groupingRows={bmwGroupingRows}
                    allGroupingRows={allBmwGroupingRows}
                    groupingTitle={bmwGroupingTitle}
                    groupingModalTitle={bmwGroupingModalTitle}
                    groupingViewAllLabel={bmwGroupingViewAllLabel}
                    isCountrySelected={isCountrySelected}
                    selectedCountryCode={selectedCountryCode}
                    selectableCountryOptions={selectableCountryOptions}
                    onSelectCountry={(countryCode) => dispatch(setCountryCode(countryCode))}
                    onViewErrors={() => navigateToOperationalDataWithFilters({ statusFilters: ['FAIL', 'OVERDUE'] })}
                />
                </Spin>
            </div>
        </div>
    );
};

export default EIMExecutiveDashboardV2;
